import React, { useEffect, useMemo, useRef, useState } from "react";
import { useCookies } from 'react-cookie';
import AdminSharedPresetPanel from "./AdminSharedPresetPanel";
import HoldAdminManualModal from "./HoldAdminManualModal";
import OpenPositionsSnapshotSection from "./OpenPositionsSnapshotSection";
import SymbolSearchPopup from "./SymbolSearchPopup";

/** 스냅샷 버튼 기준으로 관리자 수동 모달을 띄울 때 고정 위치용 */
const snapshotAnchorRect = (el) => {
  if (typeof el?.getBoundingClientRect !== 'function') return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width, height: r.height };
};

const HOLD_ACCESS_STORAGE_KEY = 'holdAccessContext';

function formatEscapeRefPx(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '0';
  if (n >= 1) return n.toFixed(4);
  return n.toFixed(8);
}

function parseEscapeRefPxInput(raw) {
  const trimmed = String(raw ?? '').trim().replace(/,/g, '');
  if (!trimmed) return 0;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : NaN;
}

const getCookieValue = (name) => {
  if (typeof document === 'undefined') {
    return '';
  }

  const targetCookie = document.cookie
    .split('; ')
    .find((item) => item.startsWith(`${name}=`));

  if (!targetCookie) {
    return '';
  }

  return decodeURIComponent(targetCookie.split('=').slice(1).join('='));
};

const getStoredHoldAccessContext = () => {
  try {
    const storedValue = sessionStorage.getItem(HOLD_ACCESS_STORAGE_KEY);
    if (!storedValue) {
      return { uid: '', from_admin: false };
    }

    const parsedValue = JSON.parse(storedValue);
    return {
      uid: String(parsedValue?.uid || ''),
      from_admin:
        parsedValue?.from_admin === 1 ||
        parsedValue?.from_admin === '1' ||
        parsedValue?.from_admin === true,
    };
  } catch (error) {
    return { uid: '', from_admin: false };
  }
};

const getInitialHoldAccessContext = () => {
  const params = new URLSearchParams(window.location.search);
  const queryUid = params.get('uid') || '';
  const queryFromAdmin = params.get('from_admin') === '1';
  const adminTargetUidCookie = getCookieValue('admin_target_uid');

  if (queryUid || queryFromAdmin) {
    return {
      uid: queryUid || adminTargetUidCookie,
      from_admin: queryFromAdmin && Boolean(adminTargetUidCookie),
    };
  }

  const storedContext = getStoredHoldAccessContext();
  if (!storedContext.from_admin) {
    return { uid: '', from_admin: false };
  }

  if (!adminTargetUidCookie) {
    return { uid: '', from_admin: false };
  }

  return {
    uid: adminTargetUidCookie || storedContext.uid,
    from_admin: true,
  };
};


export default function SetPage(){

    const [cookies] = useCookies(['uuid']);
    const [initialHoldAccess] = useState(() => getInitialHoldAccessContext());
    const [isAdminAccess, setIsAdminAccess] = useState(() => initialHoldAccess.from_admin);
    const [targetUid, setTargetUid] = useState(() => initialHoldAccess.uid || '');
    const [adminManualTradeAllowed, setAdminManualTradeAllowed] = useState(false);
    const [adminManualModal, setAdminManualModal] = useState(null);
    const [snapshotReloadToken, setSnapshotReloadToken] = useState(0);

    // ── Hold 진입 제한 (심볼별, 관리자 전용) ──
    const HOLD_DAY_LABELS = ['월', '화', '수', '목', '금', '토', '일'];
    const HOLD_RESTRICT_PRESETS = [
        { label: '토12~일21', start_day: 5, start_time: '12:00', end_day: 6, end_time: '21:00' },
        { label: '토00~일24', start_day: 5, start_time: '00:00', end_day: 6, end_time: '23:59' },
        { label: '금23~월06', start_day: 4, start_time: '23:00', end_day: 0, end_time: '06:00' },
        { label: '일00~일24', start_day: 6, start_time: '00:00', end_day: 6, end_time: '23:59' },
    ];
    const [holdRestrictModalOpen, setHoldRestrictModalOpen] = useState(false);
    const [holdRestrictDraft, setHoldRestrictDraft] = useState(null);
    const [holdRestrictSaveLoading, setHoldRestrictSaveLoading] = useState(false);
    const [holdRestrictSaveMsg, setHoldRestrictSaveMsg] = useState('');
    const [holdRestrictPresets, setHoldRestrictPresets] = useState([]);
    const [holdRestrictSelectedPresetId, setHoldRestrictSelectedPresetId] = useState('');
    const [holdRestrictPresetName, setHoldRestrictPresetName] = useState('');
    const [holdRestrictPresetMsg, setHoldRestrictPresetMsg] = useState('');

    const openHoldRestrictModal = async () => {
        if (!isAdminAccess || !symbol || symbol === 'default') return;
        setHoldRestrictSaveMsg('');
        try {
            const res = await fetchHold(`/hold/restrict_schedule?symbol=${encodeURIComponent(symbol)}`);
            const data = await res.json();
            setHoldRestrictDraft({
                restrict_enabled:    data.found ? !!data.restrict_enabled : false,
                restrict_start_day:  data.restrict_start_day  ?? 5,
                restrict_start_time: data.restrict_start_time ?? '12:00',
                restrict_end_day:    data.restrict_end_day    ?? 6,
                restrict_end_time:   data.restrict_end_time   ?? '21:00',
            });
        } catch {
            setHoldRestrictDraft({ restrict_enabled: false, restrict_start_day: 5, restrict_start_time: '12:00', restrict_end_day: 6, restrict_end_time: '21:00' });
        }
        // 프리셋 목록 로드
        fetchHold('/admin/hold_restrict_presets')
            .then((r) => r.json())
            .then((data) => { if (Array.isArray(data)) setHoldRestrictPresets(data); })
            .catch(() => {});
        setHoldRestrictSelectedPresetId('');
        setHoldRestrictPresetName('');
        setHoldRestrictPresetMsg('');
        setHoldRestrictModalOpen(true);
    };

    const closeHoldRestrictModal = () => {
        setHoldRestrictModalOpen(false);
        setHoldRestrictDraft(null);
        setHoldRestrictSaveMsg('');
        setHoldRestrictPresets([]);
        setHoldRestrictSelectedPresetId('');
        setHoldRestrictPresetName('');
        setHoldRestrictPresetMsg('');
    };

    const handleHoldRestrictDraftChange = (field) => (e) => {
        const val = field === 'restrict_enabled'
            ? e.target.checked
            : (field === 'restrict_start_day' || field === 'restrict_end_day')
                ? parseInt(e.target.value, 10)
                : e.target.value;
        setHoldRestrictDraft((prev) => ({ ...prev, [field]: val }));
    };

    const handleHoldRestrictSave = async () => {
        if (!holdRestrictDraft) return;
        setHoldRestrictSaveLoading(true);
        setHoldRestrictSaveMsg('');
        try {
            const res = await fetchHold('/hold/restrict_schedule', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ symbol, both_mode, ...holdRestrictDraft }),
            });
            const data = await res.json();
            if (res.ok && data.ok) {
                setHoldRestrictSaveMsg('✅ 저장 완료');
                setTimeout(() => { setHoldRestrictSaveMsg(''); closeHoldRestrictModal(); }, 1200);
            } else {
                setHoldRestrictSaveMsg(`❌ 저장 실패: ${data.error || res.status}`);
            }
        } catch (e) {
            setHoldRestrictSaveMsg(`❌ 오류: ${e.message}`);
        } finally {
            setHoldRestrictSaveLoading(false);
        }
    };
    // ─────────────────────────────────────────

    const showAdminManualTrade = useMemo(
        () => Boolean(isAdminAccess && adminManualTradeAllowed),
        [isAdminAccess, adminManualTradeAllowed]
    );

    // 쿠키에서 UUID를 이용하여 사용자 정보 가져오기
    const[uid,setUID] = useState(0);
    const[nickname,setNickname] = useState('');

    const [isChecked, setIsChecked] = useState(false);

    const [symbolList, setSymbolList] = useState([]);

    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [popupAnchor, setPopupAnchor] = useState(null);
    const [isSymbolMenuOpen, setIsSymbolMenuOpen] = useState(false);
    const [isModeMenuOpen, setIsModeMenuOpen] = useState(false);
    const [isBothMenuOpen, setIsBothMenuOpen] = useState(false);
    const symbolDropdownRef = useRef(null);
    const modeDropdownRef = useRef(null);
    const bothDropdownRef = useRef(null);
    const welfareRatioChangedByUser = useRef(false);
    const didInitSearch = useRef(false);
    const buildHoldUrl = (url) => {
      const normalizedUid = String(targetUid ?? '').trim();
      const params = new URLSearchParams();

      if (normalizedUid) {
        params.set('uid', normalizedUid);
      }

      if (isAdminAccess) {
        params.set('from_admin', '1');
      }

      const query = params.toString();
      if (!query) {
        return url;
      }

      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}${query}`;
    };
    const fetchHold = (url, options = {}) =>
      fetch(buildHoldUrl(url), {
        credentials: 'include',
        ...options,
      });

    useEffect(() => {
      if (isAdminAccess) {
        const nextContext = JSON.stringify({
          uid: targetUid || '',
          from_admin: 1,
        });
        sessionStorage.setItem(HOLD_ACCESS_STORAGE_KEY, nextContext);
      } else {
        sessionStorage.removeItem(HOLD_ACCESS_STORAGE_KEY);
      }

      if (window.location.search) {
        window.history.replaceState(window.history.state, document.title, window.location.pathname);
      }
    }, [isAdminAccess, targetUid]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (symbolDropdownRef.current && !symbolDropdownRef.current.contains(event.target)) {
          setIsSymbolMenuOpen(false);
        }
        if (modeDropdownRef.current && !modeDropdownRef.current.contains(event.target)) {
          setIsModeMenuOpen(false);
        }
        if (bothDropdownRef.current && !bothDropdownRef.current.contains(event.target)) {
          setIsBothMenuOpen(false);
        }
      };

      const handleEscapeKey = (event) => {
        if (event.key === 'Escape') {
          setIsSymbolMenuOpen(false);
          setIsModeMenuOpen(false);
          setIsBothMenuOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }, []);
  
    useEffect(() => {
      fetchUserInfo();
    }, [isAdminAccess]);

    const redirectToLogin = () => {
      alert('로그인 정보가 존재하지 않습니다. 로그인 페이지로 이동합니다.');
      window.location.href = isAdminAccess ? '/AdminLogin' : '/LoginAts';
    };
    
    useEffect(() => {
      if (!showAdminManualTrade) setAdminManualModal(null);
    }, [showAdminManualTrade]);

    const fetchUserInfo = () => {
      fetchHold(`/holdversion_selected`)
        .then(async (res) => {
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error(`[holdversion_selected] ${data.message || '페이지 접근 권한이 없습니다.'}`);
          }
          return data;
        })
        .then(data => {
          const nextIsAdminAccess = Boolean(data?.is_admin_access);
          setIsAdminAccess(nextIsAdminAccess);
          setTargetUid(String(data?.uid || ''));
          setAdminManualTradeAllowed(Boolean(data?.admin_manual_trade_allowed));

          if (!nextIsAdminAccess) {
            document.cookie = 'admin_target_uid=; path=/; max-age=0; SameSite=Lax';
          }

          if (data.message !== 'ok') {
            alert(data.message);
            window.location.href = nextIsAdminAccess ? '/Adminpage' : '/SelectPage';
            return;
          }

          // 권한 OK → 유저 정보 요청
          return fetchHold('/GetUserInfo');
        })
        .then(async (res) => {
          if (!res) {
            return null;
          }

          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error(`[GetUserInfo] ${data.message || '사용자 정보 조회에 실패했습니다.'}`);
          }

          return data;
        })
        .then(user => {
          if (!user) {
            return;
          }
          const nextIsAdminAccess = Boolean(user?.is_admin_access);
          setIsAdminAccess(nextIsAdminAccess);
          setUID(user.uid);
          setTargetUid(String(user.uid || ''));
          setNickname(user.nickname);
          fetchLists();
        })
        .catch(err => {
          const message = err?.message || '로그인 정보가 존재하지 않습니다. 로그인 페이지로 이동합니다.';
          console.error('SetpageHold fetchUserInfo error:', err);
          alert(message);

          if (message.includes('접근권한')) {
            window.location.href = isAdminAccess ? '/Adminpage' : '/SelectPage';
            return;
          }

          window.location.href = isAdminAccess ? '/AdminLogin' : '/LoginAts';
        });
    };
    
    // selectedlevel, entry, waiting list 받아오기
    const fetchLists = () => {
      fetchHold(`/startcheck/searchlevel`)
        .then(response => {
          if (!response.ok) {
            throw new Error('서버 응답 실패');
          }
          return response.json();
        })
        .then(data => {
          if(data[0].super_user === "8"){
            setLevelCheck(data[0].super_user);  
          }else{
            setLevelCheck(data[0].selectedLevel);
          }
        })
        .catch(error => {
          console.error('Entry List 가져오기 오류:', error);
          alert(error);
        });

      fetchHold(`/startcheck_hold/list_entry`)
        .then(response => {
          if (!response.ok) {
            throw new Error('서버 응답 실패');
          }
          return response.json();
        })
        .then(data => {
          updateList(data, e_items, setE_Items);
        })
        .catch(error => {
          console.error('Entry List 가져오기 오류:', error);
          alert(error);
        });
  
      fetchHold(`/startcheck_hold/list_wait`)
        .then(response => {
          if (!response.ok) {
            throw new Error('서버 응답 실패');
          }
          return response.json();
        })
        .then(data => {
          updateList(data, w_items, setW_Items);
        })
        .catch(error => {
          console.error('Waiting List 가져오기 오류:', error);
          alert(error);
        });
    };
  
    const updateList = (data, list, setList) => {
      for (let i = 0; i < data.length; i++) {
        if (!isItemInList(data[i].symbol, list)) {
          setList(prevList => [...prevList, data[i].symbol]);
        }
      }
    };

    const isItemInList = (item, list) => {
      return list.includes(item);
    };

    const[seed,setSeed] = useState('basic');

    const[both_mode,setBothMode] = useState('single');
    const [snapshotHasSecondAccount, setSnapshotHasSecondAccount] = useState(false);

    const[mode,setMode] = useState('normal');

    const[auto_count,setSymbolCount] = useState(3);
    const[top_bottom,setTopBottom] = useState('top');
    const[fluctuation,setFluctuation] = useState(0);

    const[funding_rate,setFundingRate] = useState('disable');
    const[f_percent_low,setFpercentLow] = useState(-100);
    const[f_percent_high,setFpercentHigh] = useState(0);

    const[symbol,setSymbol] = useState('default');
    const selectableSymbols = useMemo(
      () => Array.from(new Set(symbolList.filter((sym) => sym && sym !== 'default'))),
      [symbolList]
    );
    const currentSymbolLabel = symbol && symbol !== 'default' ? symbol : 'symbol select';
    const MODE_OPTIONS = [
      { value: 'normal', label: '일반모드 셋팅' },
      { value: 'auto', label: '자동모드 셋팅' },
    ];
    const currentModeLabel = MODE_OPTIONS.find((option) => option.value === mode)?.label || '일반모드 셋팅';

    const BOTH_MODE_OPTIONS = [
      { value: 'single', label: 'single mode' },
      { value: 'double', label: 'double mode' },
    ];
    const currentBothModeLabel = BOTH_MODE_OPTIONS.find((option) => option.value === both_mode)?.label || 'single mode';
    const isAutoMode = mode === 'auto';
    const autoDisabled = mode !== 'auto';
    const autoFieldStyle = autoDisabled
      ? { filter: 'blur(5px)', pointerEvents: 'none', opacity: 0.5 }
      : {};

    useEffect(() => {
      if (mode === 'auto') {
        setIsSymbolMenuOpen(false);
      }
    }, [mode]);

    const[full_mode,setFullMode] = useState('extension');
    const[first_entry_mode,setFirstEntryMode] = useState('immediate');
    const[escape_mode,setEscapeMode] = useState('off');
    const[escape_auto_refresh_gap_pct,setEscapeAutoRefreshGapPct] = useState(0);
    const[escape_hedge_losscut_pct,setEscapeHedgeLosscutPct] = useState(0);
    const[escape_same_bar_entry_extra,setEscapeSameBarEntryExtra] = useState(2);
    const[escapeHighPx,setEscapeHighPx] = useState('0');
    const[escapeLowPx,setEscapeLowPx] = useState('0');
    const[escapeLossPreviewLong,setEscapeLossPreviewLong] = useState('0');
    const[escapeLossPreviewShort,setEscapeLossPreviewShort] = useState('0');

    const[leverage_info, setLeverage] = useState(20);

    const[sensitivity,setSensitivity] = useState(2);
    const[atr_period,setAtrPeriod] = useState(2);
    const[sensitivity_short,setSensitivityShort] = useState(2);
    const[atr_period_short,setAtrPeriodShort] = useState(2);

    const[entry_mode,setEntryMode] = useState('kinpoint');
    const[hedge_mode,setHedgeMode] = useState('kinpoint');

    const[hedge_out_checking,setHedgeOutChecking] = useState(-1);
    const[hedge_out_candle,setHedgeOutCandle] = useState('1h');

    const[entry_continue,setEntryContinue] = useState('disable');

    const[entry_standard,setEntryStandard] = useState('disable');
    const[candle_standard,setCandleStandard] = useState('4h');
    const[candle_type_standard, setCandleTypeStandard] = useState('haikin');
    const[sensitivity_standard,setSensitivityStandard] = useState(2);
    const[atr_period_standard,setAtrPeriodStandard] = useState(2);

    const[ema_standard,setEmaStandard] = useState(200);
    /** 최초진입 2nd 보조기준 */
    const[entry_standard_2nd,setEntryStandard2nd] = useState('disable');
    const[candle_standard_2nd,setCandleStandard2nd] = useState('4h');
    const[candle_type_standard_2nd,setCandleTypeStandard2nd] = useState('haikin');
    const[sensitivity_standard_2nd,setSensitivityStandard2nd] = useState(2);
    const[atr_period_standard_2nd,setAtrPeriodStandard2nd] = useState(2);
    const[ema_standard_2nd,setEmaStandard2nd] = useState(200);
    
    const[ema_losscut,setEmaLosscut] = useState('no');
    const[ema_hedge_gap_ratio,setEmaHedgeGapRatio] = useState(0);
    //////////////////////////////////////////////////////////
    const[circle_entry_candle,setCircleEntryCandle] = useState('1h');
    const[sensitivity_circle_entry,setSensitivityCircleEntry] = useState(1);
    const[atr_period_circle_entry,setAtrPeriodCircleEntry] = useState(20);
    const[sensitivity_circle_entry_short,setSensitivityCircleEntryShort] = useState(1);
    const[atr_period_circle_entry_short,setAtrPeriodCircleEntryShort] = useState(20);
    const[candle_type_circle_entry, setCandleTypeCircleEntry] = useState('haikin');
    const[circle_entry_checking,setCircleEntryChecking] = useState(-1);
    const[circle_entry_mode,setCircleEntryMode] = useState('kinpoint');
    /////////////circle hedge///////////////////////////////
    const[circle_hedge_checking,setCircleHedgeChecking] = useState(-1);
    const[circle_hedge_mode,setCircleHedgeMode] = useState('kinpoint');
    const[circle_hedge_candle,setCircleHedgeCandle] = useState('1h');
    //////////////////////////////////////////////////////////
    const[circle_candle,setCircleCandle] = useState('1h');
    const[sensitivity_circle,setSensitivityCircle] = useState(1);
    const[atr_period_circle,setAtrPeriodCircle] = useState(20);
    const[sensitivity_circle_short,setSensitivityCircleShort] = useState(1);
    const[atr_period_circle_short,setAtrPeriodCircleShort] = useState(20);
    const[candle_type_circle, setCandleTypeCircle] = useState('haikin');
    const[circle_checking,setCircleChecking] = useState(-1);
    const[circle_close_mode,setCircleCloseMode] = useState('kinpoint');
    const[circle_benefit_mode,setCircleBenefitMode] = useState('absolute');
    ///////////////////////////////////////////////////
    
    const[entry_candle,setEntryCandle] = useState('1h');

    const[entry_checking,setEntryChecking] = useState(-1);
    const[standard_checking,setStandardChecking] = useState(-1);
    const[close_checking,setCloseChecking] = useState(-1);

    const[close_mode,setCloseMode] = useState('kinpoint');

    const[start_ratio,setStartRatio] = useState(14);
    const[confirmed_ratio,setConfirmedRatio] = useState(50);
    const[max_ratio,setMaxRatio] = useState(50);

    const[hedge_candle,setHedgeCandle] = useState('1h');
    const[hedge_checking,setHedgeChecking] = useState(-1);

    const[entry_hold_mode,setEntryHoldMode] = useState('disable');
    const[entry_hold_ratio,setEntryHoldRatio] = useState(0);
    const[hedge_hold_mode,setHedgeHoldMode] = useState('disable');
    const[hedge_hold_ratio,setHedgeHoldRatio] = useState(0);
    //////////////////////////////////////////////////////////

    const[close_candle,setCloseCandle] = useState('1h');
    const[sensitivity_close,setSensitivityClose] = useState(1);
    const[atr_period_close,setAtrPeriodClose] = useState(1);
    const[sensitivity_close_short,setSensitivityCloseShort] = useState(1);
    const[atr_period_close_short,setAtrPeriodCloseShort] = useState(1);

    const[direction,setDirection] = useState('long');

    //////////////////////////////////////////////////////////

    const[candle_type_entry, setCandleTypeEntry] = useState('haikin');
    const[candle_type_close, setCandleTypeClose] = useState('haikin');

    const[benefit_ratio,setBenefitRatio] = useState(0.3);

    const[trigger_mode,setTriggerMode] = useState('no');
    const[trigger_ratio,setTriggerRatio] = useState(1);

    const[main_balance,setMainBalance] = useState(0);
    const[second_balance,setSecondBalance] = useState(0);

    const[total_benefit_usdt,setTotalBenefitUsdt] = useState(0);
    const[total_benefit_usdt_obj,setTotalBenefitUsdtObj] = useState(0);
    const[total_benefit_count,setTotalBenefitCount] = useState(0);
    const[total_current_count,setTotalCurrentCount] = useState(0);
    const[total_benefit_start,setTotalBenefitStart] = useState(0);
    const[total_continue_mode,setTotalContinueMode] = useState('disable');
    const[total_continue_direction,setTotalContinueDirection] = useState('both');
    const[single_tp_continue_mode,setSingleTpContinueMode] = useState('disable');


    const[entry_usdt_1, setEntryUsdt_1] = useState(10);
    const[welfare_mode, setWelfareMode] = useState('disable');
    const[welfare_ratio, setWelfareRatio] = useState(100);

    const[circle_mode,setCircleMode] = useState('entry');
    const[circle_rate,setCircleRate] = useState(0);
    const[circle_rate_interval,setCircleRateInterval] = useState(0);

    const[double_benefit, setDoubleBenefit] = useState(0);
    const[double_pnl, setDoublePnl] = useState(0);
    const[double_continue, setDoubleContinue] = useState('disable');

    const[totalpnl,setTotalPnl] = useState(0);

    const[hedge_continue,setHedgeContinue] = useState('no');

    //////////////////////////////////////////////////////////

    const[symbolCountValid, setSymbolCountValid] = useState(true);
    const[fPercentLowValid, setFpercentLowValid] = useState(true);
    const[fPercentHighValid, setFpercentHighValid] = useState(true);
    const[fluctuationValid, setFluctuationValid] = useState(true);
    
    const[leverageValid, setLeverageValid] = useState(true);

    const[emaStandardValid, setEmaStandardValid] = useState(true);

    const[sensitivityStandardValid, setSensitivityStandardValid] = useState(true);
    const[atrperiodStandardValid, setAtrperiodStandardValid] = useState(true);

    const[emaStandard2ndValid, setEmaStandard2ndValid] = useState(true);
    const[sensitivityStandard2ndValid, setSensitivityStandard2ndValid] = useState(true);
    const[atrperiodStandard2ndValid, setAtrperiodStandard2ndValid] = useState(true);

    const[sensitivityValid, setSensitivityValid] = useState(true);
    const[atrperiodValid, setAtrperiodValid] = useState(true);

    const[sensitivityShortValid, setSensitivityShortValid] = useState(true);
    const[atrperiodShortValid, setAtrperiodShortValid] = useState(true);
  
    const[sensitivityCircleValid, setSensitivityCircleValid] = useState(true);
    const[atrperiodCircleValid, setAtrperiodCircleValid] = useState(true);
    const[sensitivityCircleShortValid, setSensitivityCircleShortValid] = useState(true);
    const[atrperiodCircleShortValid, setAtrperiodCircleShortValid] = useState(true);

    const[sensitivityCircleEntryValid, setSensitivityCircleEntryValid] = useState(true);
    const[atrperiodCircleEntryValid, setAtrperiodCircleEntryValid] = useState(true);
    const[sensitivityCircleEntryShortValid, setSensitivityCircleEntryShortValid] = useState(true);
    const[atrperiodCircleEntryShortValid, setAtrperiodCircleEntryShortValid] = useState(true);

    const[sensitivityCloseValid, setSensitivityCloseValid] = useState(true);
    const[atrperiodCloseValid, setAtrperiodCloseValid] = useState(true);
    const[sensitivityCloseShortValid, setSensitivityCloseShortValid] = useState(true);
    const[atrperiodCloseShortValid, setAtrperiodCloseShortValid] = useState(true);

    const[benefit_ratioratioValid, setBenefitRatioratioValid] = useState(true);

    const[start_ratioValid, setStartRatioValid] = useState(true);
    const[confirmed_ratioValid, setConfirmedRatioValid] = useState(true);
    const[max_ratioValid, setMaxRatioValid] = useState(true);

    const[total_benefit_usdtValid, setTotalBenefitusdtValid] = useState(true);
    const[total_benefit_countValid, setTotalBenefitCountValid] = useState(true);

    const[trigger_ratioValid, setTriggerRatioValid] = useState(true);

    const[welfare_ratioValid, setWelfareRatioValid] = useState(true);

    const[entryusdtValid_1,setEntryUsdtValid_1] = useState(true);

    const[circle_rateValid,setCircleRateValid] = useState(true);
    const[circle_rate_intervalValid,setCircleRateIntervalValid] = useState(true);

    const[double_benefitValid, setDoubleBenefitValid] = useState(true);

    const[entry_hold_ratioValid, setEntryHoldRatioValid] = useState(true);
    const[hedge_hold_ratioValid, setHedgeHoldRatioValid] = useState(true);

    const[setlevel, setLevelCheck] = useState(0);

    const[notAllow, setNotAllow] = useState(true);

    const[e_items, setE_Items] = useState([]);
    const[w_items, setW_Items] = useState([]);

    const toggleModeMenu = () => {
      setIsModeMenuOpen((prev) => !prev);
    };

    const handleModeSelect = (value) => {
      setIsModeMenuOpen(false);
      if (value === mode) {
        return;
      }
      setMode(value);
      if (value === 'auto') {
        onClickSearch_auto();
      }
    };

    const toggleSymbolMenu = () => {
      if (mode === 'auto') {
        return;
      }
      setIsSymbolMenuOpen((prev) => !prev);
    };

    const handleSymbolSelect = (value) => {
      if (mode === 'auto') {
        return;
      }
      setSymbol(value);
      if (value && value !== 'default') {
        onClickSearch(value);
      }
      setIsSymbolMenuOpen(false);
    };

    const toggleBothMenu = () => {
      setIsBothMenuOpen((prev) => !prev);
    };

    const handleBothMenuSelect = async (value) => {
      if (value === "double") {
        const valid = await validateSecondApi();
        if (!valid) {
          setIsBothMenuOpen(false);
          return;
        }
      }
      handleBothMode(value);
      setIsBothMenuOpen(false);
    };

    const handleCheckboxChange = (event) => {
      setIsChecked(event.target.checked);
    };

    const applyTotalBenefitStart = (value) => {
      if (value !== undefined && value !== null) {
        setTotalBenefitStart(value);
      }
    };

    const[trade_start,setTradeStart] = useState(false);
    const[benefit_stop,setBenefit_stop] = useState(false);
    const[benefit_cancel,setBenefit_cancel] = useState(false);

    const[currentCoin_stop,setCurrentCoin_stop] = useState(false);
    const[allCoin_stop,setAllCoin_stop] = useState(false);

    const[end,setEnd] = useState(false);
    
    // 유효성 검사 //// 유효성 검사 //// 유효성 검사 //// 유효성 검사 //// 유효성 검사 //// 유효성 검사 //

    const validateSecondApi = async () => {
      try {
        const response = await fetchHold("/hedge/validate-second");
        const text = await response.text();
        let payload;
        try {
          payload = JSON.parse(text);
        } catch {
          payload = { message: text };
        }
        if (!response.ok || !payload?.ok) {
          throw new Error(payload.message || "SelectPage에서 유효한 2nd API 키를 등록하세요.");
        }
        if (payload.message) {
          alert(payload.message);
        }
        return true;
      } catch (error) {
        console.error(error);
        alert(error.message || "SelectPage에서 유효한 2nd API 키를 등록하세요.");
        return false;
      }
    };

    const handleBothMode = (input) => {
      const nextValue = typeof input === 'string' ? input : input?.target?.value;
      if (!nextValue) {
        return;
      }
      setBothMode(nextValue);
    };

    const handleSymbolCount = (e) => {
      const newcount = e.target.value.trim();
      setSymbolCount(newcount);
        const regex = 
        /^\d*$/;
        if (regex.test(newcount)) {
          setSymbolCountValid(true);
          } 
        else {
          setSymbolCountValid(false);
          }
        };

    const handleTopBotton = (e) => {
      setTopBottom(e.target.value);
        };

    const handleFundingRate = (e) => {
      setFundingRate(e.target.value);
        };

    const handleFpercentLow = (e) => {
      const newPercent = e.target.value.trim();
      setFpercentLow(newPercent);

      const regex = /^-?\d*(\.\d+)?$/; // ✅ 정수와 실수(음수 포함) 허용
      if (regex.test(newPercent)) {
        setFpercentLowValid(true);
      } else {
        setFpercentLowValid(false);
      }
    };

    const handleFpercentHigh = (e) => {
      const newPercent = e.target.value.trim();
      setFpercentHigh(newPercent);

      const regex = /^-?\d*(\.\d+)?$/; // ✅ 정수와 실수(음수 포함) 허용
      if (regex.test(newPercent)) {
        setFpercentHighValid(true);
      } else {
        setFpercentHighValid(false);
      }
    };

    const handleFluctuation = (e) => {
      const newPercent = e.target.value.trim();
      setFluctuation(newPercent);

      const regex = /^-?\d*(\.\d+)?$/; // ✅ 정수와 실수(음수 포함) 허용
      if (regex.test(newPercent)) {
        setFluctuationValid(true);
      } else {
        setFluctuationValid(false);
      }
    };

    const handleFullMode = (e) => {
      setFullMode(e.target.value);
        };

    const handleFirstEntryMode = (e) => {
      setFirstEntryMode(e.target.value);
        };

    const handleEscapeMode = (e) => {
      setEscapeMode(e.target.value);
    };

    const handleEscapeAutoRefreshGapPct = (e) => {
      const raw = e.target.value;
      if (raw === '') {
        setEscapeAutoRefreshGapPct(0);
        return;
      }
      const num = parseFloat(raw);
      setEscapeAutoRefreshGapPct(Number.isNaN(num) || num < 0 ? 0 : num);
    };

    const handleEscapeHedgeLosscutPct = (e) => {
      const raw = e.target.value;
      if (raw === '') {
        setEscapeHedgeLosscutPct(0);
        return;
      }
      const num = parseFloat(raw);
      setEscapeHedgeLosscutPct(Number.isNaN(num) || num < 0 ? 0 : num);
    };

    const handleEscapeSameBarEntryExtra = (e) => {
      const raw = e.target.value;
      if (raw === '') {
        setEscapeSameBarEntryExtra(0);
        return;
      }
      const num = parseInt(raw, 10);
      setEscapeSameBarEntryExtra(Number.isNaN(num) || num < 0 ? 0 : Math.min(num, 99));
    };

    const applyEscapeRefPxFromRow = (row) => {
      if (!row) return;
      setEscapeHighPx(formatEscapeRefPx(row.escape_last_long_entry_px));
      setEscapeLowPx(formatEscapeRefPx(row.escape_last_short_entry_px));
      setEscapeLossPreviewLong(formatEscapeRefPx(row.escape_loss_preview_long));
      setEscapeLossPreviewShort(formatEscapeRefPx(row.escape_loss_preview_short));
    };

    const handleEscapeRefPxRefresh = () => {
      if (symbol === 'default') {
        alert('코인을 먼저 선택하세요!');
        return;
      }
      onClickSearch(symbol);
    };

    const handleEscapePxApply = async (target) => {
      if (symbol === 'default') {
        alert('코인을 먼저 선택하세요!');
        return;
      }
      if (!isEscapeModeActive) {
        return;
      }
      const raw = target === 'long' ? escapeHighPx : escapeLowPx;
      const value = parseEscapeRefPxInput(raw);
      const label = target === 'long' ? '최고가(롱 기준가)' : '최저가(숏 기준가)';
      if (!Number.isFinite(value) || value < 0) {
        alert(`${label}에 0 이상의 숫자를 입력하세요.`);
        return;
      }
      const displayPx = formatEscapeRefPx(value);
      const action = value === 0
        ? `${label}를 0으로 초기화`
        : `${label}를 ${displayPx}(으)로 설정`;
      if (!window.confirm(`${symbol} ${action}할까요?`)) {
        return;
      }
      try {
        const response = await fetchHold('/hold/escape_px_set', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ symbol, target, value }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.ok) {
          alert(data.message || '기준가 저장에 실패했습니다.');
          return;
        }
        if (target === 'long') {
          setEscapeHighPx(formatEscapeRefPx(value));
        } else {
          setEscapeLowPx(formatEscapeRefPx(value));
        }
        alert(data.message);
      } catch (error) {
        console.error('escape_px_set error:', error);
        alert('기준가 저장 중 오류가 발생했습니다.');
      }
    };

    const resolveEscapeForcedFields = (mode = escape_mode) => {
      if (mode === 'off') return {};
      return {
        entry_mode: 'kinpoint',
        full_mode: 'extension',
        first_entry_mode: 'immediate',
        entry_checking: -1,
      };
    };

    const applyEscapeModeEntryDefaults = (mode = escape_mode) => {
      const forced = resolveEscapeForcedFields(mode);
      if (!Object.keys(forced).length) return;
      setEntryMode(forced.entry_mode);
      setFullMode(forced.full_mode);
      setFirstEntryMode(forced.first_entry_mode);
      setEntryChecking(forced.entry_checking);
    };

    const isEscapeModeActive = escape_mode !== 'off';

    const handleEntryMode = (e) => {
      setEntryMode(e.target.value);
        };
    
    const handleHedgeMode = (e) => {
      setHedgeMode(e.target.value);
        };

    const handleHedgeOutChecking = (e) => {
      setHedgeOutChecking(e.target.value);
        };

    const handleHedgeOutCandle = (e) => {
      setHedgeOutCandle(e.target.value);
        };

    const handleEntryCandle = (e) => {
      setEntryCandle(e.target.value);
        };

    const handleEntryContinue = (e) => {
      setEntryContinue(e.target.value);
        };
    
    const handleEntryStandard = (e) => {
      setEntryStandard(e.target.value);
    };

    const handleCandleStandard = (e) => {
      setCandleStandard(e.target.value);
    };

    const handleCandleTypeStandard = (e) => {
      setCandleTypeStandard(e.target.value);
    };

    const handleEmaLosscut = (e) => {
      setEmaLosscut(e.target.value);
        };

    const handleEmaHedgeGapRatio = (e) => {
      const v = parseFloat(e.target.value);
      setEmaHedgeGapRatio(Number.isFinite(v) ? v : 0);
    };

    const handleEntryChecking = (e) => {
      setEntryChecking(e.target.value);
        };

    const handleStandardChecking = (e) => {
      setStandardChecking(e.target.value);
        };
    
    const handleEntryHoldMode = (e) => {
      setEntryHoldMode(e.target.value);
        };

    const handleHedgeHoldMode = (e) => {
      setHedgeHoldMode(e.target.value);
        };

    const handleCircleChecking = (e) => {
      setCircleChecking(e.target.value);
        };
    
    const handleCloseChecking = (e) => {
      setCloseChecking(e.target.value);
        };

    const handleCircleCloseMode = (e) => {
      setCircleCloseMode(e.target.value);
        };
    
    const handleCloseMode = (e) => {
      setCloseMode(e.target.value);
        };

    const handleCircleEntryCandle = (e) => {
      setCircleEntryCandle(e.target.value);
        };
    
    const handleCircleHedgeMode = (e) => {
      setCircleHedgeMode(e.target.value);
        };
    
    const handleCircleHedgeCandle = (e) => {
      setCircleHedgeCandle(e.target.value);
        };
    
    const handleCircleHedgeChecking = (e) => {
      setCircleHedgeChecking(e.target.value);
        };
    
    const handleCandleTypeCircleEntry = (e) => {
      setCandleTypeCircleEntry(e.target.value);
        };

    const handleCircleEntryChecking = (e) => {
      setCircleEntryChecking(e.target.value);
        };

    const handleCircleEntryMode = (e) => {
      setCircleEntryMode(e.target.value);
        };

    const handleCircleCandle = (e) => {
      setCircleCandle(e.target.value);
        };
    
    const handleCircleBenefitMode = (e) => {
      setCircleBenefitMode(e.target.value);
        };

    const handleCloseCandle = (e) => {
      setCloseCandle(e.target.value);
        };

    const handleHedgeChecking = (e) => {
      setHedgeChecking(e.target.value);
        };
            
    const handleHedgeCandle = (e) => {
      setHedgeCandle(e.target.value);
        };
    
    const handleDirection = (e) => {
      setDirection(e.target.value);
        };
    
    const handleCandleTypeEntry = (e) => {
      setCandleTypeEntry(e.target.value);
        };

    
    const handleCandleTypeCircle = (e) => {
      setCandleTypeCircle(e.target.value);
        };

    const handleCandleTypeClose = (e) => {
      setCandleTypeClose(e.target.value);
        };
    
    const handleCircleMode = (e) => {
      setCircleMode(e.target.value);
        };

    const handleHedgeContinue = (e) => {
      setHedgeContinue(e.target.value);
        };

    const handleTriggerMode = (e) => {
      setTriggerMode(e.target.value);
        };

    const handleWelfareMode = (e) => {
      setWelfareMode(e.target.value);
        };

    const handleDoubleContinue = (e) => {
      setDoubleContinue(e.target.value);
        };
      
    const handleTotalContinueMode = (e) => {
      setTotalContinueMode(e.target.value);
        };
    
    const handleTotalContinueDirection = (e) => {
      setTotalContinueDirection(e.target.value);
        };

    const handleSingleTpContinueMode = (e) => {
      setSingleTpContinueMode(e.target.value);
        };
        
    const handleLeverage = (e) => {
      const newLeverage = e.target.value.trim();
      setLeverage(newLeverage);
        const regex = 
        /^\d*$/;
        if (regex.test(newLeverage)) {
          setLeverageValid(true);
          } 
        else {
          setLeverageValid(false);
          }
        };

    const handleSensitivity = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivity(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityValid(true);
          } 
        else {
          setSensitivityValid(false);
          }
        };
    
    const handleAtrperiod = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriod(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodValid(true);
          } 
        else {
          setAtrperiodValid(false);
          }
        };
      
    const handleSensitivityShort = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivityShort(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityShortValid(true);
          } 
        else {
          setSensitivityShortValid(false);
          }
        };
    
    const handleAtrperiodShort = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriodShort(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodShortValid(true);
          } 
        else {
          setAtrperiodShortValid(false);
          }
        };

    const handleEmaStandard = (e) => {
      const newemastandard = e.target.value.trim();
      setEmaStandard(newemastandard);
        const regex = 
        /^\d*$/;
        if (regex.test(newemastandard)) {
          setEmaStandardValid(true);
          } 
        else {
          setEmaStandardValid(false);
          }
        };

    const handleSensitivityStandard = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivityStandard(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityStandardValid(true);
          } 
        else {
          setSensitivityStandardValid(false);
          }
        };
    
    const handleAtrperiodStandard = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriodStandard(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodStandardValid(true);
          } 
        else {
          setAtrperiodStandardValid(false);
          }
        };

    const handleEntryStandard_2nd = (e) => {
      setEntryStandard2nd(e.target.value);
    };

    const handleCandleStandard_2nd = (e) => {
      setCandleStandard2nd(e.target.value);
    };

    const handleCandleTypeStandard_2nd = (e) => {
      setCandleTypeStandard2nd(e.target.value);
    };

    const handleEmaStandard_2nd = (e) => {
      const v = e.target.value.trim();
      setEmaStandard2nd(v);
      const regex = /^\d*$/;
      setEmaStandard2ndValid(regex.test(v));
    };

    const handleSensitivityStandard_2nd = (e) => {
      const v = e.target.value.trim();
      setSensitivityStandard2nd(v);
      const regex = /^\d+$/;
      setSensitivityStandard2ndValid(regex.test(v));
    };

    const handleAtrperiodStandard_2nd = (e) => {
      const v = e.target.value.trim();
      setAtrPeriodStandard2nd(v);
      const regex = /^\d+$/;
      setAtrperiodStandard2ndValid(regex.test(v));
    };

    const handleSensitivityCircle = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivityCircle(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityCircleValid(true);
          } 
        else {
          setSensitivityCircleValid(false);
          }
        };
      
    const handleAtrperiodCircle = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriodCircle(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodCircleValid(true);
          } 
        else {
          setAtrperiodCircleValid(false);
          }
        };

    const handleSensitivityCircleShort = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivityCircleShort(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityCircleShortValid(true);
          } 
        else {
          setSensitivityCircleShortValid(false);
          }
        };
    
    const handleAtrperiodCircleShort = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriodCircleShort(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodCircleShortValid(true);
          } 
        else {
          setAtrperiodCircleShortValid(false);
          }
        };

    const handleSensitivityCircleEntry = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivityCircleEntry(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityCircleEntryValid(true);
          } 
        else {
          setSensitivityCircleEntryValid(false);
          }
        };
      
    const handleAtrperiodCircleEntry = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriodCircleEntry(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodCircleEntryValid(true);
          } 
        else {
          setAtrperiodCircleEntryValid(false);
          }
        };

    const handleSensitivityCircleEntryShort = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivityCircleEntryShort(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityCircleEntryShortValid(true);
          } 
        else {
          setSensitivityCircleEntryShortValid(false);
          }
        };
      
    const handleAtrperiodCircleEntryShort = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriodCircleEntryShort(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodCircleEntryShortValid(true);
          } 
        else {
          setAtrperiodCircleEntryShortValid(false);
          }
        };

    const handleSensitivityClose = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivityClose(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityCloseValid(true);
          } 
        else {
          setSensitivityCloseValid(false);
          }
        };
    
    const handleAtrperiodClose = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriodClose(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodCloseValid(true);
          } 
        else {
          setAtrperiodCloseValid(false);
          }
        };

    const handleSensitivityCloseShort = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivityCloseShort(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityCloseShortValid(true);
          } 
        else {
          setSensitivityCloseShortValid(false);
          }
        };
    
    const handleAtrperiodCloseShort = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriodCloseShort(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodCloseShortValid(true);
          } 
        else {
          setAtrperiodCloseShortValid(false);
          }
        };

    const handleBenefitratio = (e) => {
      const newBenefitratio = e.target.value.trim();
      setBenefitRatio(newBenefitratio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newBenefitratio)) {
          setBenefitRatioratioValid(true);
          } 
        else {
          setBenefitRatioratioValid(false);
          }
        };

    const handleCircleRatio = (e) => {
      const newCircleratio = e.target.value.trim();
      setCircleRate(newCircleratio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newCircleratio)) {
          setCircleRateValid(true);
          } 
        else {
          setCircleRateValid(false);
          }
        };
    
    const handleCircleRateInterval = (e) => {
      const newCircleRateInterval = e.target.value.trim();
      setCircleRateInterval(newCircleRateInterval);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newCircleRateInterval)) {
          setCircleRateIntervalValid(true);
          } 
        else {
          setCircleRateIntervalValid(false);
          }
        };
    
    const handleTriggerratio = (e) => {
      const newTriggerratio = e.target.value.trim();
      setTriggerRatio(newTriggerratio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newTriggerratio)) {
          setTriggerRatioValid(true);
          } 
        else {
          setTriggerRatioValid(false);
          }
        };
    
    const handleWelfareRatio = (e) => {
      const newWelfareRatio = e.target.value.trim();
      setWelfareRatio(newWelfareRatio);
      welfareRatioChangedByUser.current = true;
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newWelfareRatio)) {
          setWelfareRatioValid(true);
          } 
        else {
          setWelfareRatioValid(false);
          }
        };

    const handleTotalBenefitCount = (e) => {
      const newTotalBenefitCount = e.target.value.trim();
      setTotalBenefitCount(newTotalBenefitCount);
        const regex = 
        /^\d+$/;
        if (regex.test(newTotalBenefitCount)) {
          setTotalBenefitCountValid(true);
          } 
        else {
          setTotalBenefitCountValid(false);
          }
        };
      
    const handleStartRatio = (e) => {
      const newStartratio = e.target.value.trim();
      setStartRatio(newStartratio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newStartratio)) {
          setStartRatioValid(true);
          } 
        else {
          setStartRatioValid(false);
          }
        };
    
    const handleConfirmedRatio = (e) => {
      const newConfirmedratio = e.target.value.trim();
      setConfirmedRatio(newConfirmedratio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newConfirmedratio)) {
          setConfirmedRatioValid(true);
          } 
        else {
          setConfirmedRatioValid(false);
          }
        };

    const handleMaxRatio = (e) => {
      const newMaxratio = e.target.value.trim();
      setMaxRatio(newMaxratio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newMaxratio)) {
          setMaxRatioValid(true);
          } 
        else {
          setMaxRatioValid(false);
          }
        };

    /////////////////////////////////////////////////////////////////
    /////////////////////////////////////////////////////////////////

    // 진입 USDT 유효성 검사 //// 진입 USDT 유효성 검사 //// 진입 USDT 유효성 검사 //
    const handleEntryUsdt_1 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_1(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_1(true);
          } 
        else {
          setEntryUsdtValid_1(false);
          }
        };
  
    const handleDoubleBenefit = (e) => {
      const newEntrybenefit = e.target.value.trim();
      setDoubleBenefit(newEntrybenefit);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntrybenefit)) {
          setDoubleBenefitValid(true);
          } 
        else {
          setDoubleBenefitValid(false);
          }
        };
    
    const handleTotalBenefitusdt = (e) => {
      const newTotalBenefitusdt = e.target.value.trim();
      setTotalBenefitUsdt(newTotalBenefitusdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newTotalBenefitusdt)) {
          setTotalBenefitusdtValid(true);
          } 
        else {
          setTotalBenefitusdtValid(false);
          }
        };
    
    const handleEntryHoldRatio = (e) => {
      const newEntryHoldRatio = e.target.value.trim();
      setEntryHoldRatio(newEntryHoldRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryHoldRatio)) {
          setEntryHoldRatioValid(true);
          }
        };
    
    const handleHedgeHoldRatio = (e) => {
      const newHedgeHoldRatio = e.target.value.trim();
      setHedgeHoldRatio(newHedgeHoldRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newHedgeHoldRatio)) {
          setHedgeHoldRatioValid(true);
          }
        };
        
    const handleAddSymbol = (symbol) => {
      const confirmed = window.confirm(`${symbol}을 리스트에 추가할까요?`);
      if (confirmed) {
      fetchHold(`/add_user_symbols_hold_front?symbol=${symbol}`)
      .then(res => res.json())
      .then(data => {
        if (data.msg) {
            alert(data.msg); // msg가 존재할 때만 출력
            return;
          }
        setSymbolList(data.signal)
      })
      .catch(err => console.error(err));
      }
    };

    const deleteSymbol = (symbol) => {
      if (symbol === "default") {
        alert('코인을 먼저 선택하세요!');
        return; // Stop execution if the condition is met
      }
      const confirmed = window.confirm(`${symbol}이 포지션이 걸려 있다면 리스트에서 삭제 후 수동으로 포지션 정리해주세요! ${symbol}을 삭제하시겠습니까?`);
      if (confirmed) {
      fetchHold(
        `/delete_user_symbols_hold?symbol=${encodeURIComponent(symbol)}&both_mode=${encodeURIComponent(both_mode)}`
      )
      .then(res => res.json())
      .then(data => setSymbolList(data.signal))
      .catch(err => console.error(err));
      removeListItem(symbol, setE_Items);
      removeListItem(symbol, setW_Items);
      setSymbol('default')
      }
    };

    const handleOpenPopup = (event) => {
      if (mode === 'auto') {
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const popupWidth = 300;
      const popupHeight = 320;

      const desiredTop = rect.top + scrollTop - popupHeight - 16;
      const top = Math.max(scrollTop + 20, desiredTop);
      const minLeft = scrollLeft + 16;
      const maxLeft = scrollLeft + viewportWidth - popupWidth - 16;
      const candidateLeft = rect.left + scrollLeft;
      const left = Math.min(Math.max(candidateLeft, minLeft), maxLeft);

      setPopupAnchor({ top, left });
      setIsPopupOpen(true);
    };

    const handleClosePopup = () => {
      setIsPopupOpen(false);
      setPopupAnchor(null);
    };        

    const addListItem = (newItem, listSetter) => {
      listSetter(prevList => [...prevList, newItem]);
    };

    const removeListItem = (itemName, listSetter) => {
      listSetter(prevList => prevList.filter(item => item !== itemName));
    };
    
    const onClickCoin = (coin) => {
      setSymbol(coin);
      onClickSearch(coin);
    }

    useEffect(() => {

      if(symbolCountValid && fPercentLowValid && fPercentHighValid && fluctuationValid && leverageValid && emaStandardValid &&
        sensitivityValid && atrperiodValid && sensitivityStandardValid && atrperiodStandardValid &&
        emaStandard2ndValid && sensitivityStandard2ndValid && atrperiodStandard2ndValid && sensitivityCloseValid &&
        atrperiodCloseValid && sensitivityCloseShortValid && atrperiodCloseShortValid &&
        sensitivityCircleEntryValid && atrperiodCircleEntryValid && sensitivityCircleEntryShortValid && atrperiodCircleEntryShortValid &&
        sensitivityCircleValid && atrperiodCircleValid && sensitivityCircleShortValid && atrperiodCircleShortValid &&
        benefit_ratioratioValid && entryusdtValid_1 && circle_rateValid && circle_rate_intervalValid && trigger_ratioValid && double_benefitValid && total_benefit_usdtValid && total_benefit_countValid && welfare_ratioValid && start_ratioValid && confirmed_ratioValid && max_ratioValid &&
        entry_hold_ratioValid && hedge_hold_ratioValid
        ) {
        setNotAllow(false);
        return;
      }
      setNotAllow(true);
    }, [symbolCountValid, fPercentLowValid, fPercentHighValid, fluctuationValid, leverageValid, emaStandardValid,
       sensitivityValid, atrperiodValid, sensitivityStandardValid, atrperiodStandardValid,
       emaStandard2ndValid, sensitivityStandard2ndValid, atrperiodStandard2ndValid, sensitivityCloseValid,
       atrperiodCloseValid, sensitivityCloseShortValid, atrperiodCloseShortValid,
       sensitivityCircleEntryValid, atrperiodCircleEntryValid, sensitivityCircleEntryShortValid, atrperiodCircleEntryShortValid,
       sensitivityCircleValid, atrperiodCircleValid, sensitivityCircleShortValid, atrperiodCircleShortValid,
       benefit_ratioratioValid, entryusdtValid_1, circle_rateValid, circle_rate_intervalValid, trigger_ratioValid, double_benefitValid, total_benefit_usdtValid, total_benefit_countValid, welfare_ratioValid, start_ratioValid, confirmed_ratioValid, max_ratioValid,
       entry_hold_ratioValid, hedge_hold_ratioValid
      ]);

    useEffect(() => {
        fetchHold(`/get_user_symbols_hold`)
        .then(res => res.json())
        .then(data => setSymbolList(data.signal))
        .catch(err => console.error(err));
    },[symbol,targetUid]);

    useEffect(() => {
      if (entry_standard === 'disable' && direction === 'auto') {
        setDirection('long');   // 강제 변경
      }
    }, [entry_standard]);
  
    useEffect(() => {
      if (didInitSearch.current) return;
      if (!targetUid && !uid) return;
      if (setlevel === 0 || setlevel === '0') return;
      didInitSearch.current = true;
      onClickSearch('default');
    }, [targetUid, uid, setlevel]);

    useEffect(() => {
      if (!welfareRatioChangedByUser.current) return; // 사용자 입력이 아닌 경우 스킵
      if (main_balance === 0 || second_balance === 0) {
        alert('메인 잔고 또는 세컨드 잔고가 0원입니다. 잔고를 확인하고 진행하세요!');
        return;
      }
      const ratio = parseFloat(welfare_ratio);
      setEntryUsdt_1((main_balance + second_balance) * ratio / 100);
    }, [welfare_ratio, main_balance, second_balance]);

    const onClickSamaria_check = () => {
      window.open('/SamariaSignals', '_blank');
      return;
    }

    const onClickSearch_auto = () => {
      fetchHold(`/setdata_first_hold`)
      .then((response) => {
        if(!response.ok) {
          throw new Error('400 아니면 500 에러남');
        }
        return response.json()
      })
      .then((result) => {
        if (!result || result.length === 0) {
        console.log("데이터가 비어있음, 그냥 넘어감");
        return; // 아무것도 안 함
      }

        const data = result[0]; // 안전하게 변수로 받음
        console.log('db에서 성공적으로 데이터 받아옴');

        setFullMode(data.full_mode);
        setFirstEntryMode(data.first_entry_mode || 'immediate');
        setEscapeMode(data.escape_mode || 'off');
        setEscapeAutoRefreshGapPct(parseFloat(data.escape_auto_refresh_gap_pct) || 0);
        setEscapeHedgeLosscutPct(parseFloat(data.escape_hedge_losscut_pct) || 0);
        setEscapeSameBarEntryExtra(
          data.escape_same_bar_entry_extra !== undefined && data.escape_same_bar_entry_extra !== null
            ? Math.max(0, parseInt(data.escape_same_bar_entry_extra, 10) || 0)
            : 2
        );
        applyEscapeRefPxFromRow(data);
        setBothMode(data.both_mode);
        setSymbolCount(data.auto_count);
        setTopBottom(data.top_bottom);
        setFluctuation(data.fluctuation)
        setFundingRate(data.funding_rate);
        setFpercentLow(data.f_percent_low);
        setFpercentHigh(data.f_percent_high);

        setEntryMode(data.entry_mode);
        setHedgeMode(data.hedge_mode);
        setHedgeOutChecking(data.hedge_out_checking);
        setHedgeOutCandle(data.hedge_out_candle || data.circle_entry_candle);
        setLeverage(data.leverage_info);
        setEntryCandle(data.entry_candle);
        setCloseCandle(data.close_candle);

        setEntryChecking(data.entry_checking);
        setStandardChecking(data.standard_checking);
        setCloseChecking(data.close_checking);

        setCloseMode(data.close_mode);

        setStartRatio(data.start_ratio);
        setConfirmedRatio(data.confirmed_ratio);
        setMaxRatio(data.max_ratio);

        setSensitivity(data.sensitivity);
        setAtrPeriod(data.atr_period);
        setSensitivityShort(data.sensitivity_short);
        setAtrPeriodShort(data.atr_period_short);

        setEntryContinue(data.entry_continue);
        setEntryStandard(data.entry_standard);
        setEmaStandard(result[0].ema_standard);
        setEmaLosscut(result[0].ema_losscut);
        setEmaHedgeGapRatio(result[0].ema_hedge_gap_ratio != null ? Number(result[0].ema_hedge_gap_ratio) : 0);
        setCandleStandard(data.candle_standard);
        setCandleTypeStandard(data.candle_type_standard);
        setSensitivityStandard(data.sensitivity_standard);
        setAtrPeriodStandard(data.atr_period_standard);

        setEntryStandard2nd(data.entry_standard_2nd != null ? data.entry_standard_2nd : 'disable');
        setEmaStandard2nd(data.ema_standard_2nd != null ? data.ema_standard_2nd : 200);
        setCandleStandard2nd(data.candle_standard_2nd != null ? data.candle_standard_2nd : '4h');
        setCandleTypeStandard2nd(data.candle_type_standard_2nd != null ? data.candle_type_standard_2nd : 'haikin');
        setSensitivityStandard2nd(data.sensitivity_standard_2nd != null ? data.sensitivity_standard_2nd : 2);
        setAtrPeriodStandard2nd(data.atr_period_standard_2nd != null ? data.atr_period_standard_2nd : 2);

        setHedgeCandle(data.hedge_candle || data.entry_candle);
        setHedgeChecking(data.hedge_checking);

        setCircleEntryCandle(data.circle_entry_candle);
        setSensitivityCircleEntry(data.sensitivity_circle_entry);
        setAtrPeriodCircleEntry(data.atr_period_circle_entry);
        setSensitivityCircleEntryShort(data.sensitivity_circle_entry_short);
        setAtrPeriodCircleEntryShort(data.atr_period_circle_entry_short);
        setCandleTypeCircleEntry(data.candle_type_circle_entry);
        setCircleEntryChecking(data.circle_entry_checking);
        setCircleEntryMode(data.circle_entry_mode);

        setCircleHedgeChecking(data.circle_hedge_checking);
        setCircleHedgeMode(data.circle_hedge_mode);
        setCircleHedgeCandle(data.circle_hedge_candle || data.circle_entry_candle);

        setCircleCandle(data.circle_candle);
        setSensitivityCircle(data.sensitivity_circle);
        setAtrPeriodCircle(data.atr_period_circle);
        setSensitivityCircleShort(data.sensitivity_circle_short);
        setAtrPeriodCircleShort(data.atr_period_circle_short);
        setCandleTypeCircle(data.candle_type_circle);
        setCircleChecking(data.circle_checking);
        setCircleCloseMode(data.circle_close_mode);

        setSensitivityClose(data.sensitivity_close);
        setAtrPeriodClose(data.atr_period_close);
        setSensitivityCloseShort(data.sensitivity_close_short);
        setAtrPeriodCloseShort(data.atr_period_close_short);

        setDirection(data.direction);

        setBenefitRatio(data.benefit_ratio);

        setTriggerMode(data.trigger_mode);
        setTriggerRatio(data.trigger_ratio);

        setWelfareMode(data.welfare_mode);
        setWelfareRatio(data.welfare_ratio);
        setEntryUsdt_1(data.entry_usdt_1);

        setCircleMode(data.circle_mode);
        setCircleRate(data.circle_rate);
        setCircleRateInterval(data.circle_rate_interval);
        setCircleBenefitMode(data.circle_benefit_mode);

        setDoubleBenefit(data.double_benefit);
        setDoubleContinue(data.double_continue);

        setCandleTypeEntry(data.candle_type_entry);
        setCandleTypeClose(data.candle_type_close);

        setEntryHoldRatio(data.entry_hold_ratio);
        setEntryHoldMode(data.entry_hold_mode);
        setHedgeHoldRatio(data.hedge_hold_ratio);
        setHedgeHoldMode(data.hedge_hold_mode);

      })
      .catch((error) => {
        console.error('에러남:', error.message || error);
      })
    }

    const onClickSearch = (symbol) => {
      let urlName
      const str = symbol;
      console.log(str);
      if(str === 'default'){
        urlName = 'default'
      }else{
        const index = str.indexOf('/');
        urlName = str.substring(0,index);
      }  
      fetchHold(`/setdata_hold/${urlName}/${setlevel}`)
      .then((response) => {
        if(!response.ok) {
          throw new Error('400 아니면 500 에러남');
        }
        return response.json()
      })
      .then((result) => {
        const data = result[0]; // 안전하게 변수로 받음
        console.log('db에서 성공적으로 데이터 받아옴');
        setFullMode(data.full_mode);
        setFirstEntryMode(data.first_entry_mode || 'immediate');
        setEscapeMode(data.escape_mode || 'off');
        setEscapeAutoRefreshGapPct(parseFloat(data.escape_auto_refresh_gap_pct) || 0);
        setEscapeHedgeLosscutPct(parseFloat(data.escape_hedge_losscut_pct) || 0);
        setEscapeSameBarEntryExtra(
          data.escape_same_bar_entry_extra !== undefined && data.escape_same_bar_entry_extra !== null
            ? Math.max(0, parseInt(data.escape_same_bar_entry_extra, 10) || 0)
            : 2
        );
        applyEscapeRefPxFromRow(data);
        setBothMode(data.both_mode);
        setEntryMode(data.entry_mode);
        setHedgeMode(data.hedge_mode);
        setHedgeOutChecking(data.hedge_out_checking);
        setHedgeOutCandle(data.hedge_out_candle || data.circle_entry_candle);
        setLeverage(data.leverage_info);
        setEntryCandle(data.entry_candle);
        setCloseCandle(data.close_candle);

        setEntryChecking(data.entry_checking);
        setStandardChecking(data.standard_checking);
        setCloseChecking(data.close_checking);

        setCloseMode(data.close_mode);

        setStartRatio(data.start_ratio);
        setConfirmedRatio(data.confirmed_ratio);
        setMaxRatio(data.max_ratio);

        setSensitivity(data.sensitivity);
        setAtrPeriod(data.atr_period);
        setSensitivityShort(data.sensitivity_short);
        setAtrPeriodShort(data.atr_period_short);

        setEntryContinue(data.entry_continue);
        setEntryStandard(data.entry_standard);
        setEmaStandard(result[0].ema_standard);
        setEmaLosscut(result[0].ema_losscut);
        setEmaHedgeGapRatio(result[0].ema_hedge_gap_ratio != null ? Number(result[0].ema_hedge_gap_ratio) : 0);
        setCandleStandard(data.candle_standard);
        setCandleTypeStandard(data.candle_type_standard);
        setSensitivityStandard(data.sensitivity_standard);
        setAtrPeriodStandard(data.atr_period_standard);

        setEntryStandard2nd(data.entry_standard_2nd != null ? data.entry_standard_2nd : 'disable');
        setEmaStandard2nd(data.ema_standard_2nd != null ? data.ema_standard_2nd : 200);
        setCandleStandard2nd(data.candle_standard_2nd != null ? data.candle_standard_2nd : '4h');
        setCandleTypeStandard2nd(data.candle_type_standard_2nd != null ? data.candle_type_standard_2nd : 'haikin');
        setSensitivityStandard2nd(data.sensitivity_standard_2nd != null ? data.sensitivity_standard_2nd : 2);
        setAtrPeriodStandard2nd(data.atr_period_standard_2nd != null ? data.atr_period_standard_2nd : 2);

        setHedgeCandle(data.hedge_candle || data.entry_candle);
        setHedgeChecking(data.hedge_checking);

        setCircleEntryCandle(data.circle_entry_candle);
        setSensitivityCircleEntry(data.sensitivity_circle_entry);
        setAtrPeriodCircleEntry(data.atr_period_circle_entry);
        setSensitivityCircleEntryShort(data.sensitivity_circle_entry_short);
        setAtrPeriodCircleEntryShort(data.atr_period_circle_entry_short);
        setCandleTypeCircleEntry(data.candle_type_circle_entry);
        setCircleEntryChecking(data.circle_entry_checking);
        setCircleEntryMode(data.circle_entry_mode);

        setCircleHedgeChecking(data.circle_hedge_checking);
        setCircleHedgeMode(data.circle_hedge_mode);
        setCircleHedgeCandle(data.circle_hedge_candle || data.circle_entry_candle);

        setCircleCandle(data.circle_candle);
        setSensitivityCircle(data.sensitivity_circle);
        setAtrPeriodCircle(data.atr_period_circle);
        setSensitivityCircleShort(data.sensitivity_circle_short);
        setAtrPeriodCircleShort(data.atr_period_circle_short);
        setCandleTypeCircle(data.candle_type_circle);
        setCircleChecking(data.circle_checking);
        setCircleCloseMode(data.circle_close_mode);

        setSensitivityClose(data.sensitivity_close);
        setAtrPeriodClose(data.atr_period_close);
        setSensitivityCloseShort(data.sensitivity_close_short);
        setAtrPeriodCloseShort(data.atr_period_close_short);

        setDirection(data.direction);

        setBenefitRatio(data.benefit_ratio);

        setTriggerMode(data.trigger_mode);
        setTriggerRatio(data.trigger_ratio);

        setWelfareMode(data.welfare_mode);
        setWelfareRatio(data.welfare_ratio);
        setEntryUsdt_1(data.entry_usdt_1);
        
        setCircleMode(data.circle_mode);
        setCircleRate(data.circle_rate);
        setCircleRateInterval(data.circle_rate_interval);
        setCircleBenefitMode(data.circle_benefit_mode);

        setDoubleBenefit(data.double_benefit);
        setDoublePnl(data.double_pnl);
        setDoubleContinue(data.double_continue);

        setTotalBenefitUsdt(data.total_benefit_usdt);
        setTotalBenefitUsdtObj(data.total_benefit_usdt_obj);
        setTotalBenefitCount(data.total_benefit_count);
        setTotalCurrentCount(data.total_current_count);
        applyTotalBenefitStart(data.total_benefit_start);
        setTotalContinueMode(data.total_continue_mode);
        setTotalContinueDirection(data.total_continue_direction);
        setSingleTpContinueMode(data.single_tp_continue_mode || 'disable');

        setCandleTypeEntry(data.candle_type_entry);
        setCandleTypeClose(data.candle_type_close);

        setTradeStart(data.trade_start);
        setBenefit_stop(data.current_symbol_stop);
        setCurrentCoin_stop(data.current_coin_clear);
        setAllCoin_stop(data.all_coin_clear);

        setTotalPnl(data.totalpnl);
        setHedgeContinue(data.hedge_continue);

        setEntryHoldRatio(data.entry_hold_ratio);
        setEntryHoldMode(data.entry_hold_mode);
        setHedgeHoldRatio(data.hedge_hold_ratio);
        setHedgeHoldMode(data.hedge_hold_mode);

        const e_checklist = isItemInList(data.symbol, e_items);
        const w_checklist = isItemInList(data.symbol, w_items);
        
        if(str !== 'default'){
          result.forEach(item => {
            // console.log('item',item)
            if ((item.trade_start === 1 || item.trade_start === '1') && e_checklist === false) {
              addListItem(item.symbol, setE_Items);
            }
            if ((item.trade_start === 0 || item.trade_start === '0') && e_checklist === true) {
              removeListItem(item.symbol, setE_Items);
            }
          
            // Waiting coin 리스트 삽입 및 삭제
            if ((item.current_symbol_stop === 1 || item.current_symbol_stop === '1') && w_checklist === false) {
              addListItem(item.symbol, setW_Items);
            }
            if ((item.current_symbol_stop === 0 || item.current_symbol_stop === '0') && w_checklist === true) {
              removeListItem(item.symbol, setW_Items);
            }
          });
        };

        console.log('e_items',e_items);
        console.log('e_checklist',e_checklist);
      })
      .catch((error) => {
        console.log('에러남');
      })
    }

    const onClickAutoStart = () => {

      let confirmed = false

      confirmed = window.confirm('HOLD 버전 자동매매를 시작하시겠습니까?');

      if (confirmed) {
        console.log('스타트 버튼 클릭')
        fetchHold('/start_hold_auto', {
          method : 'POST',
          headers: {
            'Content-Type' : 'application/json',
          },
          body: JSON.stringify({
            full_mode,
            first_entry_mode,
            both_mode,
            auto_count,
            top_bottom,
            fluctuation,
            funding_rate,
            f_percent_low,
            f_percent_high,
            entry_mode,
            hedge_mode,
            hedge_out_checking,
            hedge_out_candle,
            leverage_info,
            sensitivity,
            atr_period,
            sensitivity_short,
            atr_period_short,
            entry_continue,
            entry_standard,
            ema_standard,
            ema_losscut,
            ema_hedge_gap_ratio,
            candle_standard,
            candle_type_standard,
            sensitivity_standard,
            atr_period_standard,
            entry_standard_2nd,
            ema_standard_2nd,
            candle_standard_2nd,
            candle_type_standard_2nd,
            sensitivity_standard_2nd,
            atr_period_standard_2nd,
            hedge_candle,
            hedge_checking,
            circle_entry_candle,
            sensitivity_circle_entry,
            atr_period_circle_entry,
            sensitivity_circle_entry_short,
            atr_period_circle_entry_short,
            candle_type_circle_entry,
            circle_entry_checking,
            circle_entry_mode,
            circle_hedge_checking,
            circle_hedge_mode,
            circle_hedge_candle,
            circle_candle,
            sensitivity_circle,
            atr_period_circle,
            sensitivity_circle_short,
            atr_period_circle_short,
            candle_type_circle,
            circle_checking,
            circle_close_mode,
            sensitivity_close,
            atr_period_close,
            sensitivity_close_short,
            atr_period_close_short,
            entry_candle,
            entry_checking,
            standard_checking,
            close_checking,
            close_mode,
            start_ratio,
            confirmed_ratio,
            max_ratio,
            close_candle,
            direction,
            benefit_ratio,
            trigger_mode,
            trigger_ratio,
            welfare_mode,
            welfare_ratio,
            entry_usdt_1,
            double_benefit,
            double_continue,
            circle_mode,
            circle_rate,
            circle_rate_interval,
            circle_benefit_mode,
            candle_type_entry,
            candle_type_close,
            entry_hold_ratio,
            entry_hold_mode,
            hedge_hold_ratio,
            hedge_hold_mode,
            single_tp_continue_mode,
            first_entry_mode
            }),
         })
         .then(response => response.json())  // 응답을 JSON 형식으로 변환
         .then(data => {
          alert(data.message);  // JSON에서 message 값을 가져옴
         })
         .catch(error => console.error('Error:', error));
       };

      onClickSearch_auto();
      // 첫 번째 실행 (1초 후)
      setTimeout(() => {
        window.location.reload();
      }, 10000); // 첫 실행까지 1초 딜레이
    };

    const onClickStart = () => {

      if (symbol === "default") {
        alert('코인을 먼저 선택하세요!');
        return; // Stop execution if the condition is met
      }
      
      let confirmed = false

      if (trade_start === 0 || trade_start === "0"){
        confirmed = window.confirm(symbol + '의 매매를 시작하시겠습니까?');
      }else if (trade_start === 1 || trade_start === "1"){
        confirmed = window.confirm(symbol + '의 셋팅값을 변경하시겠습니까?');
      }

      if (confirmed) {
        console.log('스타트 버튼 클릭')
        fetchHold('/start_hold', {
          method : 'POST',
          headers: {
            'Content-Type' : 'application/json',
          },
          body: JSON.stringify({
            symbol,
            entry_mode,
            hedge_mode,
            hedge_out_checking,
            hedge_out_candle,
            full_mode,
            first_entry_mode,
            both_mode,
            leverage_info,
            sensitivity,
            atr_period,
            sensitivity_short,
            atr_period_short,
            entry_continue,
            entry_standard,
            ema_standard,
            ema_losscut,
            ema_hedge_gap_ratio,
            candle_standard,
            candle_type_standard,
            sensitivity_standard,
            atr_period_standard,
            entry_standard_2nd,
            ema_standard_2nd,
            candle_standard_2nd,
            candle_type_standard_2nd,
            sensitivity_standard_2nd,
            atr_period_standard_2nd,
            hedge_candle,
            hedge_checking,
            circle_entry_candle,
            sensitivity_circle_entry,
            atr_period_circle_entry,
            sensitivity_circle_entry_short,
            atr_period_circle_entry_short,
            candle_type_circle_entry,
            circle_entry_checking,
            circle_entry_mode,
            circle_hedge_checking,
            circle_hedge_mode,
            circle_hedge_candle,
            circle_candle,
            sensitivity_circle,
            atr_period_circle,
            sensitivity_circle_short,
            atr_period_circle_short,
            candle_type_circle,
            circle_checking,
            circle_close_mode,
            sensitivity_close,
            atr_period_close,
            sensitivity_close_short,
            atr_period_close_short,
            entry_candle,
            entry_checking,
            standard_checking,
            close_checking,
            close_mode,
            start_ratio,
            confirmed_ratio,
            max_ratio,
            close_candle,
            direction,
            benefit_ratio,
            trigger_mode,
            trigger_ratio,
            welfare_mode,
            welfare_ratio,
            entry_usdt_1,
            double_benefit,
            double_continue,
            circle_mode,
            circle_rate,
            circle_rate_interval,
            circle_benefit_mode,
            candle_type_entry,
            candle_type_close,
            hedge_continue,
            entry_hold_ratio,
            entry_hold_mode,
            hedge_hold_ratio,
            hedge_hold_mode,
            single_tp_continue_mode,
            first_entry_mode,
            escape_mode,
            escape_auto_refresh_gap_pct,
            escape_hedge_losscut_pct,
            escape_same_bar_entry_extra,
            ...resolveEscapeForcedFields(),
            }),
         })
         .then(response => response.json())  // 응답을 JSON 형식으로 변환
         .then(data => {
          onClickSearch(symbol);
          alert(data.message);  // JSON에서 message 값을 가져옴
         })
         .catch(error => console.error('Error:', error));
       };
    };

    useEffect(() => {
      if (escape_mode !== 'off') {
        applyEscapeModeEntryDefaults(escape_mode);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [escape_mode]);

    const buildHoldPresetPayload = () => ({
      entry_mode,
      hedge_mode,
      hedge_out_checking,
      hedge_out_candle,
      full_mode,
      first_entry_mode,
      both_mode,
      leverage_info,
      sensitivity,
      atr_period,
      sensitivity_short,
      atr_period_short,
      entry_continue,
      entry_standard,
      ema_standard,
      ema_losscut,
      ema_hedge_gap_ratio,
      candle_standard,
      candle_type_standard,
      sensitivity_standard,
      atr_period_standard,
      entry_standard_2nd,
      ema_standard_2nd,
      candle_standard_2nd,
      candle_type_standard_2nd,
      sensitivity_standard_2nd,
      atr_period_standard_2nd,
      hedge_candle,
      hedge_checking,
      circle_entry_candle,
      sensitivity_circle_entry,
      atr_period_circle_entry,
      sensitivity_circle_entry_short,
      atr_period_circle_entry_short,
      candle_type_circle_entry,
      circle_entry_checking,
      circle_entry_mode,
      circle_hedge_checking,
      circle_hedge_mode,
      circle_hedge_candle,
      circle_candle,
      sensitivity_circle,
      atr_period_circle,
      sensitivity_circle_short,
      atr_period_circle_short,
      candle_type_circle,
      circle_checking,
      circle_close_mode,
      sensitivity_close,
      atr_period_close,
      sensitivity_close_short,
      atr_period_close_short,
      entry_candle,
      entry_checking,
      standard_checking,
      close_checking,
      close_mode,
      start_ratio,
      confirmed_ratio,
      max_ratio,
      close_candle,
      direction,
      benefit_ratio,
      trigger_mode,
      trigger_ratio,
      welfare_mode,
      welfare_ratio,
      entry_usdt_1,
      double_benefit,
      double_continue,
      circle_mode,
      circle_rate,
      circle_rate_interval,
      circle_benefit_mode,
      candle_type_entry,
      candle_type_close,
      hedge_continue,
      entry_hold_ratio,
      entry_hold_mode,
      hedge_hold_ratio,
      hedge_hold_mode,
      single_tp_continue_mode,
      first_entry_mode,
      escape_mode,
      escape_auto_refresh_gap_pct,
      escape_hedge_losscut_pct,
      escape_same_bar_entry_extra,
      ...resolveEscapeForcedFields(),
    });

    const applyHoldPreset = (payload = {}) => {
      if (payload.entry_mode !== undefined) setEntryMode(payload.entry_mode);
      if (payload.hedge_mode !== undefined) setHedgeMode(payload.hedge_mode);
      if (payload.hedge_out_checking !== undefined) setHedgeOutChecking(payload.hedge_out_checking);
      if (payload.hedge_out_candle !== undefined) setHedgeOutCandle(payload.hedge_out_candle);
      if (payload.full_mode !== undefined) setFullMode(payload.full_mode);
      if (payload.first_entry_mode !== undefined) setFirstEntryMode(payload.first_entry_mode);
      if (payload.escape_mode !== undefined) setEscapeMode(payload.escape_mode);
      if (payload.escape_auto_refresh_gap_pct !== undefined) {
        setEscapeAutoRefreshGapPct(parseFloat(payload.escape_auto_refresh_gap_pct) || 0);
      }
      if (payload.escape_hedge_losscut_pct !== undefined) {
        setEscapeHedgeLosscutPct(parseFloat(payload.escape_hedge_losscut_pct) || 0);
      }
      if (payload.escape_same_bar_entry_extra !== undefined) {
        setEscapeSameBarEntryExtra(
          Math.max(0, parseInt(payload.escape_same_bar_entry_extra, 10) || 0)
        );
      }
      if (payload.both_mode !== undefined) setBothMode(payload.both_mode);
      if (payload.leverage_info !== undefined) setLeverage(payload.leverage_info);
      if (payload.sensitivity !== undefined) setSensitivity(payload.sensitivity);
      if (payload.atr_period !== undefined) setAtrPeriod(payload.atr_period);
      if (payload.sensitivity_short !== undefined) setSensitivityShort(payload.sensitivity_short);
      if (payload.atr_period_short !== undefined) setAtrPeriodShort(payload.atr_period_short);
      if (payload.entry_continue !== undefined) setEntryContinue(payload.entry_continue);
      if (payload.entry_standard !== undefined) setEntryStandard(payload.entry_standard);
      if (payload.ema_standard !== undefined) setEmaStandard(payload.ema_standard);
      if (payload.ema_losscut !== undefined) setEmaLosscut(payload.ema_losscut);
      if (payload.ema_hedge_gap_ratio !== undefined) setEmaHedgeGapRatio(Number(payload.ema_hedge_gap_ratio) || 0);
      if (payload.candle_standard !== undefined) setCandleStandard(payload.candle_standard);
      if (payload.candle_type_standard !== undefined) setCandleTypeStandard(payload.candle_type_standard);
      if (payload.sensitivity_standard !== undefined) setSensitivityStandard(payload.sensitivity_standard);
      if (payload.atr_period_standard !== undefined) setAtrPeriodStandard(payload.atr_period_standard);
      if (payload.entry_standard_2nd !== undefined) setEntryStandard2nd(payload.entry_standard_2nd);
      if (payload.ema_standard_2nd !== undefined) setEmaStandard2nd(payload.ema_standard_2nd);
      if (payload.candle_standard_2nd !== undefined) setCandleStandard2nd(payload.candle_standard_2nd);
      if (payload.candle_type_standard_2nd !== undefined) setCandleTypeStandard2nd(payload.candle_type_standard_2nd);
      if (payload.sensitivity_standard_2nd !== undefined) setSensitivityStandard2nd(payload.sensitivity_standard_2nd);
      if (payload.atr_period_standard_2nd !== undefined) setAtrPeriodStandard2nd(payload.atr_period_standard_2nd);
      if (payload.hedge_candle !== undefined) setHedgeCandle(payload.hedge_candle);
      if (payload.hedge_checking !== undefined) setHedgeChecking(payload.hedge_checking);
      if (payload.circle_entry_candle !== undefined) setCircleEntryCandle(payload.circle_entry_candle);
      if (payload.sensitivity_circle_entry !== undefined) setSensitivityCircleEntry(payload.sensitivity_circle_entry);
      if (payload.atr_period_circle_entry !== undefined) setAtrPeriodCircleEntry(payload.atr_period_circle_entry);
      if (payload.sensitivity_circle_entry_short !== undefined) setSensitivityCircleEntryShort(payload.sensitivity_circle_entry_short);
      if (payload.atr_period_circle_entry_short !== undefined) setAtrPeriodCircleEntryShort(payload.atr_period_circle_entry_short);
      if (payload.candle_type_circle_entry !== undefined) setCandleTypeCircleEntry(payload.candle_type_circle_entry);
      if (payload.circle_entry_checking !== undefined) setCircleEntryChecking(payload.circle_entry_checking);
      if (payload.circle_entry_mode !== undefined) setCircleEntryMode(payload.circle_entry_mode);
      if (payload.circle_hedge_checking !== undefined) setCircleHedgeChecking(payload.circle_hedge_checking);
      if (payload.circle_hedge_mode !== undefined) setCircleHedgeMode(payload.circle_hedge_mode);
      if (payload.circle_hedge_candle !== undefined) setCircleHedgeCandle(payload.circle_hedge_candle);
      if (payload.circle_candle !== undefined) setCircleCandle(payload.circle_candle);
      if (payload.sensitivity_circle !== undefined) setSensitivityCircle(payload.sensitivity_circle);
      if (payload.atr_period_circle !== undefined) setAtrPeriodCircle(payload.atr_period_circle);
      if (payload.sensitivity_circle_short !== undefined) setSensitivityCircleShort(payload.sensitivity_circle_short);
      if (payload.atr_period_circle_short !== undefined) setAtrPeriodCircleShort(payload.atr_period_circle_short);
      if (payload.candle_type_circle !== undefined) setCandleTypeCircle(payload.candle_type_circle);
      if (payload.circle_checking !== undefined) setCircleChecking(payload.circle_checking);
      if (payload.circle_close_mode !== undefined) setCircleCloseMode(payload.circle_close_mode);
      if (payload.sensitivity_close !== undefined) setSensitivityClose(payload.sensitivity_close);
      if (payload.atr_period_close !== undefined) setAtrPeriodClose(payload.atr_period_close);
      if (payload.sensitivity_close_short !== undefined) setSensitivityCloseShort(payload.sensitivity_close_short);
      if (payload.atr_period_close_short !== undefined) setAtrPeriodCloseShort(payload.atr_period_close_short);
      if (payload.entry_candle !== undefined) setEntryCandle(payload.entry_candle);
      if (payload.entry_checking !== undefined) setEntryChecking(payload.entry_checking);
      if (payload.standard_checking !== undefined) setStandardChecking(payload.standard_checking);
      if (payload.close_checking !== undefined) setCloseChecking(payload.close_checking);
      if (payload.close_mode !== undefined) setCloseMode(payload.close_mode);
      if (payload.start_ratio !== undefined) setStartRatio(payload.start_ratio);
      if (payload.confirmed_ratio !== undefined) setConfirmedRatio(payload.confirmed_ratio);
      if (payload.max_ratio !== undefined) setMaxRatio(payload.max_ratio);
      if (payload.close_candle !== undefined) setCloseCandle(payload.close_candle);
      if (payload.direction !== undefined) setDirection(payload.direction);
      if (payload.benefit_ratio !== undefined) setBenefitRatio(payload.benefit_ratio);
      if (payload.trigger_mode !== undefined) setTriggerMode(payload.trigger_mode);
      if (payload.trigger_ratio !== undefined) setTriggerRatio(payload.trigger_ratio);
      if (payload.welfare_mode !== undefined) setWelfareMode(payload.welfare_mode);
      if (payload.welfare_ratio !== undefined) setWelfareRatio(payload.welfare_ratio);
      if (payload.entry_usdt_1 !== undefined) setEntryUsdt_1(payload.entry_usdt_1);
      if (payload.double_benefit !== undefined) setDoubleBenefit(payload.double_benefit);
      if (payload.double_continue !== undefined) setDoubleContinue(payload.double_continue);
      if (payload.circle_mode !== undefined) setCircleMode(payload.circle_mode);
      if (payload.circle_rate !== undefined) setCircleRate(payload.circle_rate);
      if (payload.circle_rate_interval !== undefined) setCircleRateInterval(payload.circle_rate_interval);
      if (payload.circle_benefit_mode !== undefined) setCircleBenefitMode(payload.circle_benefit_mode);
      if (payload.candle_type_entry !== undefined) setCandleTypeEntry(payload.candle_type_entry);
      if (payload.candle_type_close !== undefined) setCandleTypeClose(payload.candle_type_close);
      if (payload.hedge_continue !== undefined) setHedgeContinue(payload.hedge_continue);
      if (payload.entry_hold_ratio !== undefined) setEntryHoldRatio(payload.entry_hold_ratio);
      if (payload.entry_hold_mode !== undefined) setEntryHoldMode(payload.entry_hold_mode);
      if (payload.hedge_hold_ratio !== undefined) setHedgeHoldRatio(payload.hedge_hold_ratio);
      if (payload.hedge_hold_mode !== undefined) setHedgeHoldMode(payload.hedge_hold_mode);
      if (payload.single_tp_continue_mode !== undefined) setSingleTpContinueMode(payload.single_tp_continue_mode);
    };

  
    const onClickStop = () => {

      let confirmed = false

      confirmed = window.confirm('HOLD버전 자동매매를 종료하시겠습니까? 종료하시면 프로그램 자체가 종료되므로 수동으로 포지션 정리하셔야 합니다.');

      if (confirmed) {
        console.log('종료 버튼 클릭')
        fetchHold('/stop_hold_auto', {
          method : 'POST',
          headers: {
            'Content-Type' : 'application/json',
          },
          body: JSON.stringify({
            both_mode
            }),
         })
         .then(response => response.json())  // 응답을 JSON 형식으로 변환
         .then(data => {
          alert(data.message);  // JSON에서 message 값을 가져옴
         })
         .catch(error => console.error('Error:', error));
       };

      onClickSearch_auto();
      setTimeout(() => {
            window.location.reload();
          }, 10000); // 첫 실행까지 1초 딜레이
    };

    const onClickMainBalance = (symbol) => {
      const confirmed = window.confirm('메인 잔고를 조회할까요?');
      if (confirmed) {
        fetchHold(`/getdata_main_balance`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('main_balance에서 받아온 데이터');
          setMainBalance(result.main_balance);
        })
        .catch((error) => {
          console.log('에러남');
        })
      }
    }

    const onClickSecondBalance = (symbol) => {
      const confirmed = window.confirm('2nd 잔고를 조회할까요?');
      if (confirmed) {
        fetchHold(`/getdata_second_balance`)
        .then(async (response) => {
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            alert(data.message || '잔고 조회 오류가 발생했습니다.');
            throw new Error('400 아니면 500 에러남');
          }
          return data;
        })
        .then((result) => {
          console.log('second_balance에서 받아온 데이터');
          setSecondBalance(result.second_balance);
        })
        .catch((error) => {
          console.log('에러남');
        })
      }
    }

    const onClickDoublePnl = (symbol) => {
      const confirmed = window.confirm('현재 두 계정의 합산 코인 수익을 조회할까요?');
      if (confirmed) {
        fetchHold(`/getdata_double_pnl_hold`)
        .then(async (response) => {
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            alert(data.message || '잔고 조회 오류가 발생했습니다.');
            throw new Error('400 아니면 500 에러남');
          }
          return data;
        })
        .then((result) => {
          console.log('current_balance에서 받아온 데이터');
          setDoublePnl(result.double_pnl);
        })
        .catch((error) => {
          console.log('에러남');
        })
      }
    }

    // 종합 익절 검색
    const onClickTotal_search = () => {

      const benefitValue = parseFloat(total_benefit_usdt);

      if (isNaN(benefitValue) || benefitValue === 0) {
        window.alert('종합 익절 값을 넣어주세요!');
        return;
      }

      if (isNaN(main_balance) || main_balance === 0) {
        window.alert('메인 잔고를 먼저 조회해주세요!');
        return;
      }

      if (
          both_mode === 'double' &&
          (isNaN(second_balance) || second_balance === 0)
        ) {
          window.alert(
            '2nd 잔고가 조회되지 않았습니다.\n더블모드 적용 시 2nd 잔고를 조회해주세요!'
          );
          return;
        }

      // ✅ 여기까지 왔다는 것은 모든 조건 통과
      const total_sum = main_balance + second_balance;
      const total_balance = total_sum + (total_sum * benefitValue / 100);

      setTotalBenefitUsdtObj(total_balance);
    };

    // 종합 익절 시작
    const onClickTotal_start = (symbol) => {
      let confirmed

      const benefitValue = parseFloat(total_benefit_usdt);

      if (isNaN(benefitValue) || benefitValue === 0) {
        confirmed = window.confirm('종합 익절 값을 넣어주세요!');
      } else if (isNaN(main_balance) || main_balance === 0) {
        confirmed = window.confirm('메인 잔고를 먼저 조회해주세요!');
      } 
      else {
        if (isNaN(second_balance) || second_balance === 0) {
          confirmed = window.confirm('2nd 잔고가 조회되지 않았습니다. 더블모드 적용시 2nd 잔고를 조회해주세요!');
        }
        confirmed = window.confirm('종합 익절을 시작할까요? 혹시라도 추가 입금시에는 종합익절을 취소한 뒤 진행하세요');
      }

      if (confirmed) {
        const total_balance = main_balance + second_balance;
        fetchHold(`/setdata_total_benefit_hold/${benefitValue}/${total_balance}/${total_benefit_count}/${total_continue_mode}/${total_continue_direction}`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('total_benefit에서 받아온 데이터');
          // 🔹 서버에서 보내준 메시지를 사용자에게 보여주기
          if (result.message) {
            alert(result.message);
          }
          if (result?.message?.includes('시작')) {
            setTotalBenefitStart(1);
          }
          onClickSearch(symbol);
        })
        .catch((error) => {
          console.log('에러남');
          })
        };
    }

    // 종합 익절 취소 
    const onClickTotal_cancel = (symbol) => {
      const confirmed = window.confirm('종합 익절을 취소할까요?');

      if (confirmed) {
        fetchHold(`/setdata_total_benefit_cancel_hold`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('total_benefit_cancel에서 받아온 데이터');
          // 🔹 서버에서 보내준 메시지를 사용자에게 보여주기
          if (result.message) {
            alert(result.message);
          }
          setTotalBenefitStart(0);
          onClickSearch(symbol);
        })
        .catch((error) => {
          console.log('에러남');
        })
      };
    }

    // 익절 후 정리
    const onClickBenefit_stop = (symbol) => {
      const confirmed = window.confirm('Do you want to clean up the selected coin after profiting?');

      if (confirmed) {
        setBenefit_stop(1);
        let urlName
        const str = symbol;
        console.log(str);
        if(str === 'default'){
          urlName = 'default'
        }else{
          const index = str.indexOf('/');
          urlName = str.substring(0,index);
        }
        fetchHold(`/setdata_benefit_hold/${urlName}/${setlevel}`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('benefit에서 받아온 데이터');
          onClickSearch(symbol);
        })
        .catch((error) => {
          console.log('에러남');
          })
        };
    }

    // 익절 후 정리 취소 
    const onClickBenefit_cancel = (symbol) => {
      const confirmed = window.confirm('Do you want to cancel clearing after profiting the selected coins?');

      if (confirmed) {
        setBenefit_cancel(1);
        let urlName
        const str = symbol;
        console.log(str);
        if(str === 'default'){
          urlName = 'default'
        }else{
          const index = str.indexOf('/');
          urlName = str.substring(0,index);
        }
        fetchHold(`/setdata_benefit_cancel_hold/${urlName}/${setlevel}`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('benefit_cancel에서 받아온 데이터');
          onClickSearch(symbol);
        })
        .catch((error) => {
          console.log('에러남');
        })
      };
    }

    // 현재 코인 정리 
    const onClickCurrentCoin_stop = (symbol) => {
      const confirmed = window.confirm('현재 선택하신 코인의 포지션을 정리하시길 원하십니까? 포지션 정리시 약 5~10초 정도 지연될 수도 있습니다.');

      if (confirmed) {
        setCurrentCoin_stop(true);
        const str = symbol;
        console.log(str);
        const index = str.indexOf('/');
        const urlName = str.substring(0,index);
        console.log(both_mode);
        fetchHold(`/setdata_current_coin_clear_hold/${urlName}/${both_mode}`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('current_stop에서 받아온 데이터');
          setTimeout(() => {
            window.location.reload();
          }, 2000); // 첫 실행까지 1초 딜레이
        })
        .catch((error) => {
          console.log('에러남');
        })
      };
    }

    const getUuidFromBrowser = () => {
      const cookieUuid = cookies.uuid;
      if (cookieUuid) {
        return cookieUuid;
      }

      const fallbackCookie = document.cookie
        .split('; ')
        .find((item) => item.startsWith('uuid='));

      if (!fallbackCookie) {
        return '';
      }

      return decodeURIComponent(fallbackCookie.split('=').slice(1).join('='));
    };

    //로그아웃
    const onClickEnd = () => {
        const uuid = getUuidFromBrowser();
        if (!uuid) {
          alert('로그인 정보가 존재하지 않습니다.');
          return;
        }
        const logoutUrl = isAdminAccess ? '/super_logout' : '/logout';
        const redirectUrl = isAdminAccess ? '/AdminLogin' : '/';

        fetch(logoutUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ userId: uuid })
        })
        .then(response => {
          if (!response.ok) {
            throw new Error('로그아웃 요청 실패');
          }
          console.log('로그아웃 성공');
          alert('Logout is successful.');
          window.location.href = redirectUrl;
        })
        .catch(error => {
          console.error('로그아웃 요청 중 오류 발생:', error);
        });
      // UUID를 서버로 보내는 예시 코드
    }

    const isTotalBenefitRunning = Number(total_benefit_start) === 1;

    return (
      <main className="kp-console kp-console--hold">
        <div className="kp-console__glare" aria-hidden="true" />
        <section className="kp-console__panel">
          <header className="kp-hero">
            <p className="kp-hero__eyebrow">Bitsoop auto trade system</p>
            <h1 className="kp-hero__title">HOLD SET cockpit</h1>
            <p className="kp-hero__subtitle">
              HOLD SET 전략의 자동 셋팅을 한 화면에서 제어하세요. UID 상태와 실시간 리스트로 포지션 흐름을 즉시 확인할 수 있습니다.
            </p>
            <div className="kp-hero__meta">
              <span className="kp-hero__uid">UID {targetUid || uid || "—"}</span>
              {isAdminAccess ? (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '6px 12px',
                    borderRadius: '999px',
                    background: 'rgba(45, 212, 191, 0.1)',
                    border: '1px solid rgba(94, 234, 212, 0.35)',
                    color: '#99f6e4',
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                  }}
                >
                  관리자 연결 모드
                </span>
              ) : null}
              <span>{nickname ? `${nickname}님 환영합니다` : "계정 정보를 불러오는 중"}</span>
            </div>
          </header>

          <div className="kp-console__layout">
            <div className="kp-console__main">
              <article className="kp-card kp-card--controls">
                <div className={`kp-symbol-controls ${mode === "auto" ? "is-mode-auto" : ""}`}>
                  <div className="kp-symbol-dropdown kp-mode-dropdown" ref={modeDropdownRef}>
                    {/* <button
                      type="button"
                      className={`kp-symbol-dropdown__trigger ${isModeMenuOpen ? "is-open" : ""}`}
                      onClick={toggleModeMenu}
                      aria-haspopup="listbox"
                      aria-expanded={isModeMenuOpen}
                    >
                      <span className="kp-symbol-dropdown__current">{currentModeLabel}</span>
                      <span className="kp-symbol-dropdown__chevron" aria-hidden="true" />
                    </button> */}
                    {isModeMenuOpen && (
                      <div className="kp-symbol-dropdown__menu" role="listbox">
                        {MODE_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={`kp-symbol-dropdown__option ${option.value === mode ? "is-active" : ""}`}
                            onClick={() => handleModeSelect(option.value)}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="kp-symbol-dropdown" ref={symbolDropdownRef}>
                    <button
                      type="button"
                      className={`kp-symbol-dropdown__trigger ${isSymbolMenuOpen ? "is-open" : ""}`}
                      onClick={toggleSymbolMenu}
                      aria-haspopup="listbox"
                      aria-expanded={isSymbolMenuOpen}
                      disabled={mode === "auto"}
                    >
                      <span className="kp-symbol-dropdown__current">{currentSymbolLabel}</span>
                      <span className="kp-symbol-dropdown__chevron" aria-hidden="true" />
                    </button>
                    {isSymbolMenuOpen && (
                      <div className="kp-symbol-dropdown__menu" role="listbox">
                        <button
                          type="button"
                          className={`kp-symbol-dropdown__option ${symbol === "default" ? "is-active" : ""}`}
                          onClick={() => handleSymbolSelect("default")}
                        >
                          symbol select
                        </button>
                        {selectableSymbols.length > 0 ? (
                          <>
                            <div className="kp-symbol-dropdown__divider" />
                            {selectableSymbols.map((sym) => (
                              <button
                                key={sym}
                                type="button"
                                className={`kp-symbol-dropdown__option ${sym === symbol ? "is-active" : ""}`}
                                onClick={() => handleSymbolSelect(sym)}
                              >
                                {sym}
                              </button>
                            ))}
                          </>
                        ) : (
                          <div className="kp-symbol-dropdown__empty">등록된 심볼이 없습니다</div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="kp-symbol-dropdown kp-both-dropdown" ref={bothDropdownRef}>
                    <button
                      type="button"
                      className={`kp-symbol-dropdown__trigger ${isBothMenuOpen ? "is-open" : ""}`}
                      onClick={toggleBothMenu}
                      aria-haspopup="listbox"
                      aria-expanded={isBothMenuOpen}
                    >
                      <span className="kp-symbol-dropdown__current">{currentBothModeLabel}</span>
                      <span className="kp-symbol-dropdown__chevron" aria-hidden="true" />
                    </button>
                    {isBothMenuOpen && (
                      <div className="kp-symbol-dropdown__menu" role="listbox">
                        {BOTH_MODE_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={`kp-symbol-dropdown__option ${option.value === both_mode ? "is-active" : ""}`}
                            onClick={() => handleBothMenuSelect(option.value)}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="kp-symbol-controls__buttons">
                    <button onClick={handleOpenPopup} className="button_benefit_c_ut" disabled={mode === "auto"}>
                      추 가
                    </button>
                    <button onClick={() => deleteSymbol(symbol)} className="button_benefit_c_ut" disabled={mode === "auto"}>
                      삭 제
                    </button>
                  </div>
                </div>
                <SymbolSearchPopup
                  isOpen={isPopupOpen}
                  onAdd={handleAddSymbol}
                  onClose={handleClosePopup}
                  anchorPosition={popupAnchor}
                />
              </article>
              <AdminSharedPresetPanel
                isAdminAccess={isAdminAccess}
                featureKey="hold"
                buildPresetPayload={buildHoldPresetPayload}
                applyPreset={applyHoldPreset}
                request={fetchHold}
                reloadKey={`${targetUid}:${uid}`}
                singleRow
              />

              <article className="kp-card kp-card--form">
                <div className="contentWrap_sp">

            <div className="line"></div>

            <div className="center_text_lever">Basic setting</div>
              <div class="leverage_container">
                <div class="leverage_item">진입기준</div>
                  <select className="select_samaria" value={entry_mode}
                  onChange={handleEntryMode}
                  disabled={isEscapeModeActive}
                  style={{
                    filter: isEscapeModeActive ? 'blur(5px)' : 'none',
                  }}>
                  <option value="ut">시그널</option>
                  <option value="kinpoint">킨포인트</option>
                  </select>
                <div class="leverage_item">헷지기준</div>
                  <select className="select_samaria" value={hedge_mode}
                  onChange={handleHedgeMode}>
                  <option value="ut">시그널</option>
                  <option value="kinpoint">킨포인트</option>
                  <option value="none">미적용</option>
                  <option value="losscut">손절적용</option>
                  </select>
              </div>
              <div className="leverage_container">
                <div class="leverage_item">진입모드</div>
                  <select className="select_samaria" value={full_mode}
                  onChange={handleFullMode}
                  disabled={isEscapeModeActive}
                  style={{
                    filter: isEscapeModeActive ? 'blur(5px)' : 'none',
                  }}>
                  <option value="extension">즉시진입모드</option>
                  <option value="original">변곡진입모드</option>
                  </select>
                <div class="leverage_item">첫진입 시각</div>
                  <select className="select_samaria" value={first_entry_mode}
                  onChange={handleFirstEntryMode}
                  disabled={isEscapeModeActive}
                  style={{
                    filter: isEscapeModeActive ? 'blur(5px)' : 'none',
                  }}>
                  <option value="immediate">즉시</option>
                  <option value="nine_am">09시 모드</option>
                  </select>
                </div>
              <div class="leverage_container">
                <div class="leverage_item">탈출모드</div>
                <select className="select_samaria" value={escape_mode}
                onChange={handleEscapeMode}>
                <option value="off">미적용</option>
                <option value="escape_long">탈출모드-롱</option>
                <option value="escape_short">탈출모드-숏</option>
                </select>
                </div>
              {isEscapeModeActive && (
                <p style={{ margin: '6px 12px 10px', fontSize: '0.85em', color: '#b8860b', lineHeight: 1.45 }}>
                  탈출모드 ON: 일반 진입·청산·순환매·신규 헷지는 중단됩니다.
                  진입기준=킨포인트, 진입모드=즉시진입, 첫진입=즉시, 진입기준신호=발생으로 자동 적용됩니다(진입기준봉은 DB 설정값 유지).
                  {escape_mode === 'escape_long'
                    ? ' 숏 포지션만 스윙(직전 숏 진입가 아래 + 진입기준 신호 진입, 단일익절봉 HA 반대색 청산).'
                    : ' 롱 포지션만 스윙(직전 롱 진입가 위 + 진입기준 신호 진입, 단일익절봉 HA 반대색 청산).'}
                  {' '}만약 기존 반대편 헷지포지션이 있다면 헷지정리 신호로 정리된 뒤 추가 헷지포지션은 잡지 않습니다.
                  {Number(escape_auto_refresh_gap_pct) > 0 && (
                    escape_mode === 'escape_long'
                      ? ` 자동갱신: 추적 최저가에서 ${escape_auto_refresh_gap_pct}% 상승 시 최저가(숏 기준가)를 0으로 갱신 후 해당 구간부터 다시 계산합니다.`
                      : ` 자동갱신: 추적 최고가에서 ${escape_auto_refresh_gap_pct}% 하락 시 최고가(롱 기준가)를 0으로 갱신 후 해당 구간부터 다시 계산합니다.`
                  )}
                </p>
              )}
              {isEscapeModeActive && (
                <>
                  <div class="leverage_container">
                    <div class="leverage_item">자동갱신 이격(%)</div>
                    <input
                      type="number"
                      class="leverage_input_basic"
                      min="0"
                      step="0.1"
                      value={escape_auto_refresh_gap_pct}
                      onChange={handleEscapeAutoRefreshGapPct}
                      disabled={mode === 'auto'}
                    />
                    <div class="leverage_item">탈출헷지손절률(%)</div>
                    <input
                      type="number"
                      class="leverage_input_basic"
                      min="0"
                      step="0.01"
                      value={escape_hedge_losscut_pct}
                      onChange={handleEscapeHedgeLosscutPct}
                      disabled={mode === 'auto'}
                    />
                    <div class="leverage_item">동일봉 추가진입(회)</div>
                    <input
                      type="number"
                      class="leverage_input_basic"
                      min="0"
                      max="99"
                      step="1"
                      value={escape_same_bar_entry_extra}
                      onChange={handleEscapeSameBarEntryExtra}
                      disabled={mode === 'auto'}
                    />
                  </div>
                  <p style={{ margin: '0 12px 8px', fontSize: '0.82em', color: '#b8860b', lineHeight: 1.45 }}>
                    자동갱신 이격·탈출헷지손절률 모두 0=미적용. 손절률은 진입가 대비 가격변동%(레버리지 미적용).
                    HA 반대색 익절 청산 시 수익이면 최고가/최저가 기준가를 청산가로 갱신합니다.
                    동일봉 추가진입: 진입봉(entry_candle) 기준 1회 + 입력값(기본 2)=총 3회까지 진입 허용(0이면 1회). 청산 후 같은 익절봉(close_candle)에서는 재진입하지 않습니다.
                  </p>
                  <div class="leverage_container">
                    <div class="leverage_item">최고가(롱 기준가)</div>
                    <input
                      type="text"
                      class="leverage_input_basic"
                      value={escapeHighPx}
                      onChange={(e) => setEscapeHighPx(e.target.value)}
                      disabled={mode === 'auto' || symbol === 'default'}
                    />
                    <div class="leverage_item">최저가(숏 기준가)</div>
                    <input
                      type="text"
                      class="leverage_input_basic"
                      value={escapeLowPx}
                      onChange={(e) => setEscapeLowPx(e.target.value)}
                      disabled={mode === 'auto' || symbol === 'default'}
                    />
                  </div>
                  <div class="leverage_container">
                    <div class="leverage_item">손절 예정 최고가</div>
                    <input
                      type="text"
                      class="leverage_input_basic"
                      value={escapeLossPreviewLong}
                      readOnly
                      disabled
                    />
                    <div class="leverage_item">손절 예정 최저가</div>
                    <input
                      type="text"
                      class="leverage_input_basic"
                      value={escapeLossPreviewShort}
                      readOnly
                      disabled
                    />
                  </div>
                  <p style={{ margin: '0 12px 8px', fontSize: '0.82em', color: '#b8860b', lineHeight: 1.45 }}>
                    최고·최저가를 직접 입력 후 「적용」으로 저장합니다. 0 입력 후 적용하면 초기화됩니다. 스윙 진입·수익 익절 청산 시 자동 갱신됩니다.
                    {both_mode === 'double' && ' (double 모드: 메인·보조 계정 DB가 함께 갱신됩니다.)'}
                  </p>
                  <p style={{ margin: '0 12px 8px', fontSize: '0.82em', color: '#6a7a8a', lineHeight: 1.45 }}>
                    손절 예정값은 진입기준봉 마감 시 봇이 자동 갱신합니다. 메인 앵커 구간에서 스윙 손절 청산 시 반영될 예상 기준가이며, 직접 수정할 수 없습니다.
                    「최고가/최저가 가져오기」로 DB 최신값을 불러올 수 있습니다.
                  </p>
                  <div className="button_benefit_leverage button_benefit_leverage--circlepro" style={{ marginBottom: '10px' }}>
                    <button
                      type="button"
                      onClick={handleEscapeRefPxRefresh}
                      className="button_benefit_c_lever"
                      disabled={mode === 'auto' || symbol === 'default'}
                    >
                      최고가/최저가 가져오기
                    </button>
                    <button
                      type="button"
                      onClick={() => handleEscapePxApply('long')}
                      className="button_benefit_c_lever"
                      disabled={mode === 'auto' || symbol === 'default'}
                    >
                      최고가 적용
                    </button>
                    <button
                      type="button"
                      onClick={() => handleEscapePxApply('short')}
                      className="button_benefit_c_lever"
                      disabled={mode === 'auto' || symbol === 'default'}
                    >
                      최저가 적용
                    </button>
                  </div>
                </>
              )}
              <div className="leverage_container">
                <div class="leverage_item">진입 기준봉</div>
                  <select className="select_samaria" value={entry_candle}
                  onChange={handleEntryCandle}>
                  <option value="5m">5 분봉</option>
                  <option value="15m">15 분봉</option>
                  <option value="30m">30 분봉</option>
                  <option value="1h">1 시간봉</option>
                  <option value="4h">4 시간봉</option>
                  <option value="6h">6 시간봉</option>
                  <option value="12h">12 시간봉</option>
                  <option value="1d">1 일봉</option>
                  <option value="3d">3 일봉</option>
                  <option value="1w">1 주봉</option>
                  </select>
                <div class="leverage_item">헷지 기준봉</div>
                  <select className="select_samaria" value={hedge_candle}
                  onChange={handleHedgeCandle}>
                  <option value="5m">5 분봉</option>
                  <option value="15m">15 분봉</option>
                  <option value="30m">30 분봉</option>
                  <option value="1h">1 시간봉</option>
                  <option value="4h">4 시간봉</option>
                  <option value="6h">6 시간봉</option>
                  <option value="12h">12 시간봉</option>
                  <option value="1d">1 일봉</option>
                  <option value="3d">3 일봉</option>
                  <option value="1w">1 주봉</option>
                  </select>
                </div>
                <div className="leverage_container">
                <div class="leverage_item"style={{
                    filter: (entry_mode !== 'ut' && hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                  }} >진입/헷지 기준 캔들</div>
                <select className="select_samaria"
                  value={candle_type_entry}
                   disabled={entry_mode !== 'ut' && hedge_mode !== 'ut'}
                    style={{
                      filter: (entry_mode !== 'ut' && hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                    }}
                  onChange={handleCandleTypeEntry}>
                  <option value="haikin">하이킨아시</option>
                  <option value="normal">일반봉</option>
                </select>
                </div>
                <div className="leverage_container">
                <div class="leverage_item"
                style={{
                  filter: (entry_mode !== 'ut' && hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}>LONG Sensitivity</div>
                <input
                type="number"
                class="leverage_input_basic"
                 disabled={entry_mode !== 'ut' && hedge_mode !== 'ut'}
                style={{
                  filter: (entry_mode !== 'ut' && hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}
                value={sensitivity}
                onChange={handleSensitivity} />
                <div class="leverage_item" style={{
                    filter: (entry_mode !== 'ut' && hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                  }}>LONG ATR Period</div>
                <input
                type="number"
                class="leverage_input_basic"
                 disabled={entry_mode !== 'ut' && hedge_mode !== 'ut'}
                style={{
                  filter: (entry_mode !== 'ut' && hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}
                value={atr_period}
                onChange={handleAtrperiod} />
              </div>
              <div className="leverage_container">
                <div class="leverage_item"
                style={{
                  filter: (entry_mode !== 'ut' && hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}>SHORT Sensitivity</div>
                <input
                type="number"
                class="leverage_input_basic"
                 disabled={entry_mode !== 'ut' && hedge_mode !== 'ut'}
                style={{
                  filter: (entry_mode !== 'ut' && hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}
                value={sensitivity_short}
                onChange={handleSensitivityShort} />
                <div class="leverage_item" style={{
                    filter: (entry_mode !== 'ut' && hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                  }}>SHORT ATR Period</div>
                <input
                type="number"
                class="leverage_input_basic"
                 disabled={entry_mode !== 'ut' && hedge_mode !== 'ut'}
                style={{
                  filter: (entry_mode !== 'ut' && hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}
                value={atr_period_short}
                onChange={handleAtrperiodShort} />
              </div>
              <div className="leverage_container">
                <div class="leverage_item">진입 기준 신호 </div>
                <select className="select_samaria"
                  value={entry_checking}
                  onChange={handleEntryChecking}
                  disabled={isEscapeModeActive}
                  style={{
                    filter: isEscapeModeActive ? 'blur(5px)' : 'none',
                  }}>
                  <option value="-1">발생</option>
                  <option value="-2">확정</option>
                </select>
                <div class="leverage_item">헷지 기준 신호 </div>
                <select className="select_samaria"
                  value={hedge_checking}
                  onChange={handleHedgeChecking}>
                  <option value="-1">발생</option>
                  <option value="-2">확정</option>
                </select>
              </div>
              <div className="leverage_container">
                <div class="leverage_item">진입 보전 발동률(%)</div>
                <input
                type="number"
                class="leverage_input_basic"
                value={entry_hold_ratio}
                onChange={handleEntryHoldRatio} />
                <div class="leverage_item">진입 보전 모드</div>
                <select className="select_samaria" disabled={entry_hold_ratio === 0} value={entry_hold_mode} onChange={handleEntryHoldMode}>
                <option value="disable">수수료보전</option>
                <option value="able">수익보전</option>
                </select>
              </div>
              <div className="leverage_container">
                <div class="leverage_item">헷지 보전 발동률(%)</div>
                <input
                type="number"
                class="leverage_input_basic"
                value={hedge_hold_ratio}
                onChange={handleHedgeHoldRatio} />
                <div class="leverage_item">헷지 보전 모드</div>
                <select className="select_samaria" disabled={hedge_hold_ratio === 0} value={hedge_hold_mode} onChange={handleHedgeHoldMode}>
                <option value="disable">수수료보전</option>
                <option value="able">수익보전</option>
                </select>
              </div>
              <div className="leverage_container">
                <div class="leverage_item">트리거 기준</div>
                  <select className="select_samaria"
                  value={trigger_mode}
                  onChange={handleTriggerMode}>
                  <option value="no">미적용</option>
                  <option value="ratio">비율</option>
                  <option value="price">가격</option>
                  </select>
                <div class="leverage_item" style={{
                  filter: (trigger_mode !== 'ratio') ? 'blur(5px)' : 'none',
                }}>헷지트리거(%)</div>
                  <input
                  type="number"
                  class="leverage_input_basic"
                  disabled={trigger_mode !== 'ratio'}
                  style={{
                    filter: (trigger_mode !== 'ratio') ? 'blur(5px)' : 'none',
                  }}
                  value={trigger_ratio}
                  onChange={handleTriggerratio} />
                <div class="leverage_item">연속 진입</div>
                <select className="select_samaria"
                  value={entry_continue}
                  onChange={handleEntryContinue}>
                  <option value="disable">미적용</option>
                  <option value="able">적용</option>
                </select>
              </div>

              <div className="line"></div>

            <div className="center_text_lever">Support setting</div>
              <div className="leverage_container">
                <div class="leverage_item">보조 모드</div>
                <select className="select_samaria" value={entry_standard} onChange={handleEntryStandard}>
                <option value="ut">시그널</option>
                <option value="candle">캔들</option>
                <option value="ema">EMA</option>
                <option value="disable">미적용</option>
                </select>
                <div class="leverage_item" style={{
                  filter: (entry_standard !== 'ema') ? 'blur(5px)' : 'none',
                }}>EMA 기준봉갯수</div>
                <input
                type="number"
                class="leverage_input_basic"
                value={ema_standard}
                onChange={handleEmaStandard}
                disabled={entry_standard !== 'ema'}
                style={{
                  filter: (entry_standard !== 'ema') ? 'blur(5px)' : 'none',
                }}/>
                </div>
              <div className="leverage_container">
                <div class="leverage_item" style={{
                  filter: (entry_standard !== 'ema') ? 'blur(5px)' : 'none',
                }}>EMA 손절적용</div>
                <select className="select_samaria" value={ema_losscut}
                disabled={entry_standard !== 'ema'}
                style={{
                  filter: (entry_standard !== 'ema') ? 'blur(5px)' : 'none',
                }}
                onChange={handleEmaLosscut}>
                  <option value="no">미적용</option>
                  <option value="apply">적용</option>
                </select>
                <div class="leverage_item" style={{
                  filter: (entry_standard !== 'ema') ? 'blur(5px)' : 'none',
                }}>EMA 헷지 이격(%)</div>
                <input
                type="number"
                min="0"
                step="0.01"
                class="leverage_input_basic"
                value={ema_hedge_gap_ratio}
                onChange={handleEmaHedgeGapRatio}
                disabled={entry_standard !== 'ema'}
                style={{
                  filter: (entry_standard !== 'ema') ? 'blur(5px)' : 'none',
                }}/>
                <div class="leverage_item" style={{
                  filter: (entry_standard !== 'ema') ? 'blur(5px)' : 'none',
                  fontSize: '12px',
                  color: '#888',
                }}>0 = EMA 조건 헷지 안 함</div>
              </div>
              <div className="leverage_container">
                <div class="leverage_item" style={{
                  filter: (entry_standard !== 'ut') ? 'blur(5px)' : 'none',
                }}>보조 Sensitivity</div>
                <input
                type="number"
                class="leverage_input_basic"
                value={sensitivity_standard}
                onChange={handleSensitivityStandard}
                disabled={entry_standard !== 'ut'}
                style={{
                  filter: (entry_standard !== 'ut') ? 'blur(5px)' : 'none',
                }}/>
                <div class="leverage_item" style={{
                  filter: (entry_standard !== 'ut') ? 'blur(5px)' : 'none',
                }}>보조 ATR Period</div>
                <input
                type="number"
                class="leverage_input_basic"
                value={atr_period_standard}
                onChange={handleAtrperiodStandard}
                disabled={entry_standard !== 'ut'}
                style={{
                  filter: (entry_standard !== 'ut') ? 'blur(5px)' : 'none',
                }}/>
              </div>
              <div className="leverage_container">
                <div class="leverage_item" style={{
                  filter: (entry_standard === 'disable') ? 'blur(5px)' : 'none',
                }}>보조 시간봉</div>
                <select className="select_samaria" value={candle_standard}
                disabled={entry_standard === 'disable'}
                style={{
                  filter: (entry_standard === 'disable') ? 'blur(5px)' : 'none',
                }}
                onChange={handleCandleStandard}>
                  <option value="5m">5 분봉</option>
                  <option value="15m">15 분봉</option>
                  <option value="30m">30 분봉</option>
                  <option value="1h">1 시간봉</option>
                  <option value="4h">4 시간봉</option>
                  <option value="6h">6 시간봉</option>
                  <option value="12h">12 시간봉</option>
                  <option value="1d">1 일봉</option>
                  <option value="3d">3 일봉</option>
                  <option value="1w">1 주봉</option>
                </select>
                <div class="leverage_item" style={{
                  filter: (entry_standard === 'disable') ? 'blur(5px)' : 'none',
                }} >신호 기준</div>
                <select className="select_samaria" value={candle_type_standard}
                disabled={entry_standard === 'disable'}
                style={{
                  filter: (entry_standard === 'disable') ? 'blur(5px)' : 'none',
                }}
                onChange={handleCandleTypeStandard}>
                  <option value="haikin">하이킨아시</option>
                  <option value="normal">일반봉</option>
                </select>
              </div>

              <div className="leverage_container">
                <div class="leverage_item">2nd 보조 모드</div>
                <select className="select_samaria" value={entry_standard_2nd} onChange={handleEntryStandard_2nd}>
                  <option value="ut">시그널</option>
                  <option value="candle">캔들</option>
                  <option value="ema">EMA</option>
                  <option value="disable">미적용</option>
                </select>
                <div class="leverage_item" style={{
                  filter: (entry_standard_2nd !== 'ema') ? 'blur(5px)' : 'none',
                }}>2nd EMA 기준봉갯수</div>
                <input
                type="number"
                class="leverage_input_basic"
                value={ema_standard_2nd}
                onChange={handleEmaStandard_2nd}
                disabled={entry_standard_2nd !== 'ema'}
                style={{
                  filter: (entry_standard_2nd !== 'ema') ? 'blur(5px)' : 'none',
                }}/>
              </div>
              <div className="leverage_container">
                <div class="leverage_item" style={{
                  filter: (entry_standard_2nd !== 'ut') ? 'blur(5px)' : 'none',
                }}>2nd 보조 Sensitivity</div>
                <input
                type="number"
                class="leverage_input_basic"
                value={sensitivity_standard_2nd}
                onChange={handleSensitivityStandard_2nd}
                disabled={entry_standard_2nd !== 'ut'}
                style={{
                  filter: (entry_standard_2nd !== 'ut') ? 'blur(5px)' : 'none',
                }}/>
                <div class="leverage_item" style={{
                  filter: (entry_standard_2nd !== 'ut') ? 'blur(5px)' : 'none',
                }}>2nd 보조 ATR Period</div>
                <input
                type="number"
                class="leverage_input_basic"
                value={atr_period_standard_2nd}
                onChange={handleAtrperiodStandard_2nd}
                disabled={entry_standard_2nd !== 'ut'}
                style={{
                  filter: (entry_standard_2nd !== 'ut') ? 'blur(5px)' : 'none',
                }}/>
              </div>

              <div className="leverage_container">
                <div class="leverage_item" style={{
                  filter: (entry_standard_2nd == 'disable') ? 'blur(5px)' : 'none',
                }}>2nd 보조 시간봉</div>
                <select className="select_samaria" value={candle_standard_2nd}
                disabled={entry_standard_2nd == 'disable'}
                style={{
                  filter: (entry_standard_2nd == 'disable') ? 'blur(5px)' : 'none',
                }}
                onChange={handleCandleStandard_2nd}>
                  <option value="5m">5 분봉</option>
                  <option value="15m">15 분봉</option>
                  <option value="30m">30 분봉</option>
                  <option value="1h">1 시간봉</option>
                  <option value="4h">4 시간봉</option>
                  <option value="6h">6 시간봉</option>
                  <option value="12h">12 시간봉</option>
                  <option value="1d">1 일봉</option>
                  <option value="3d">3 일봉</option>
                  <option value="1w">1 주봉</option>
                </select>
                <div class="leverage_item" style={{
                  filter: (entry_standard_2nd == 'disable') ? 'blur(5px)' : 'none',
                }} >신호 기준</div>
                <select className="select_samaria" value={candle_type_standard_2nd}
                disabled={entry_standard_2nd == 'disable'}
                style={{
                  filter: (entry_standard_2nd == 'disable') ? 'blur(5px)' : 'none',
                }}
                onChange={handleCandleTypeStandard_2nd}>
                  <option value="haikin">하이킨아시</option>
                  <option value="normal">일반봉</option>
                </select>
              </div>

              <div className="line"></div>
              <div className="center_text_lever">Circle setting</div>
              <div class="leverage_container">
                <div class="leverage_item">순환매 진입 기준</div>
                  <select className="select_samaria"
                    value={circle_entry_mode}
                    onChange={handleCircleEntryMode}>
                    <option value="ut">시그널</option>
                    <option value="kinpoint">킨포인트</option>
                  </select>
                <div class="leverage_item">순환매 헷지 기준</div>
                  <select className="select_samaria" value={circle_hedge_mode}
                  onChange={handleCircleHedgeMode}>
                  <option value="ut">시그널</option>
                  <option value="kinpoint">킨포인트</option>
                  <option value="none">미적용</option>
                  <option value="losscut">손절적용</option>
                  </select>
              </div>
              <div class="leverage_container">
                <div class="leverage_item">순환매 진입 기준봉</div>
                  <select className="select_samaria" value={circle_entry_candle}
                  onChange={handleCircleEntryCandle}>
                  <option value="5m">5 분봉</option>
                  <option value="15m">15 분봉</option>
                  <option value="30m">30 분봉</option>
                  <option value="1h">1 시간봉</option>
                  <option value="4h">4 시간봉</option>
                  <option value="6h">6 시간봉</option>
                  <option value="12h">12 시간봉</option>
                  <option value="1d">1 일봉</option>
                  <option value="3d">3 일봉</option>
                  <option value="1w">1 주봉</option>
                  </select>
                <div class="leverage_item">순환매 헷지 기준봉</div>
                  <select className="select_samaria" value={circle_hedge_candle}
                  onChange={handleCircleHedgeCandle}>
                  <option value="5m">5 분봉</option>
                  <option value="15m">15 분봉</option>
                  <option value="30m">30 분봉</option>
                  <option value="1h">1 시간봉</option>
                  <option value="4h">4 시간봉</option>
                  <option value="6h">6 시간봉</option>
                  <option value="12h">12 시간봉</option>
                  <option value="1d">1 일봉</option>
                  <option value="3d">3 일봉</option>
                  <option value="1w">1 주봉</option>
                  </select>
                <div class="leverage_item">헷지 정리 기준봉</div>
                  <select className="select_samaria" value={hedge_out_candle}
                  onChange={handleHedgeOutCandle}>
                  <option value="5m">5 분봉</option>
                  <option value="15m">15 분봉</option>
                  <option value="30m">30 분봉</option>
                  <option value="1h">1 시간봉</option>
                  <option value="4h">4 시간봉</option>
                  <option value="6h">6 시간봉</option>
                  <option value="12h">12 시간봉</option>
                  <option value="1d">1 일봉</option>
                  <option value="3d">3 일봉</option>
                  <option value="1w">1 주봉</option>
                  </select>
              </div>
              <div class="leverage_container">
                <div class="leverage_item"style={{
                  filter: (circle_entry_mode !== 'ut' && circle_hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}>순환매 진입/헷지 기준 캔들</div>
                  <select className="select_samaria"
                    value={candle_type_circle_entry}
                    disabled={circle_entry_mode !== 'ut' && circle_hedge_mode !== 'ut'}
                    style={{
                      filter: (circle_entry_mode !== 'ut' && circle_hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                    }}
                    onChange={handleCandleTypeCircleEntry}>
                    <option value="haikin">하이킨아시</option>
                    <option value="normal">일반봉</option>
                  </select>
                </div>
              <div class="leverage_container">
                <div class="leverage_item"style={{
                  filter: (circle_entry_mode !== 'ut' && circle_hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}>LONG Sensitivity</div>
                <input
                type="number"
                step="1"
                class="leverage_input_basic"
                value={sensitivity_circle_entry}
                disabled={circle_entry_mode !== 'ut' && circle_hedge_mode !== 'ut'}
                style={{
                  filter: (circle_entry_mode !== 'ut' && circle_hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}
                onChange={handleSensitivityCircleEntry} />
                <div class="leverage_item"style={{
                  filter: (circle_entry_mode !== 'ut' && circle_hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}>LONG ATR Period</div>
                <input
                type="number"
                step="1"
                class="leverage_input_basic"
                value={atr_period_circle_entry}
                disabled={circle_entry_mode !== 'ut' && circle_hedge_mode !== 'ut'}
                style={{
                  filter: (circle_entry_mode !== 'ut' && circle_hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}
                onChange={handleAtrperiodCircleEntry} />
              </div>
              <div class="leverage_container">
                <div class="leverage_item"style={{
                  filter: (circle_entry_mode !== 'ut' && circle_hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}>SHORT Sensitivity</div>
                <input
                type="number"
                step="1"
                class="leverage_input_basic"
                value={sensitivity_circle_entry_short}
                disabled={circle_entry_mode !== 'ut' && circle_hedge_mode !== 'ut'}
                style={{
                  filter: (circle_entry_mode !== 'ut' && circle_hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}
                onChange={handleSensitivityCircleEntryShort} />
                <div class="leverage_item"style={{
                  filter: (circle_entry_mode !== 'ut' && circle_hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}>SHORT ATR Period</div>
                <input
                type="number"
                step="1"
                class="leverage_input_basic"
                value={atr_period_circle_entry_short}
                disabled={circle_entry_mode !== 'ut' && circle_hedge_mode !== 'ut'}
                style={{
                  filter: (circle_entry_mode !== 'ut' && circle_hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}
                onChange={handleAtrperiodCircleEntryShort} />
              </div>
              <div class="leverage_container">
                <div class="leverage_item">순환매 진입 기준 신호</div>
                  <select className="select_samaria"
                    value={circle_entry_checking}
                    onChange={handleCircleEntryChecking}>
                    <option value="-1">발생</option>
                    <option value="-2">확정</option>
                  </select>
                <div class="leverage_item">순환매 헷지 기준 신호</div>
                  <select className="select_samaria"
                    value={circle_hedge_checking}
                    onChange={handleCircleHedgeChecking}>
                    <option value="-1">발생</option>
                    <option value="-2">확정</option>
                  </select>
              <div class="leverage_item">순환매 헷지 정리 신호</div>
                  <select className="select_samaria"
                    value={hedge_out_checking}
                    onChange={handleHedgeOutChecking}>
                    <option value="-1">발생</option>
                    <option value="-2">확정</option>
                  </select>
              </div>
              <div class="leverage_container">
                <div class="leverage_item">순환매 비율(%)</div>
                    <input
                    type="number"
                    class="leverage_input_basic"
                    value={circle_rate}
                    onChange={handleCircleRatio} />
                <div class="leverage_item">순환매 기준 간격(%)</div>
                    <input
                    type="number"
                    class="leverage_input_basic"
                    value={circle_rate_interval}
                  onChange={handleCircleRateInterval} />
              </div>
              <div className="center_text_lever">Circle benefit setting</div>
              <div class="leverage_container">
                <div class="leverage_item">순환매 익절기준 모드</div>
                <select className="select_samaria" value={circle_benefit_mode} onChange={handleCircleBenefitMode}>
                  <option value='absolute'>절대</option>
                  <option value='signal'>신호기준</option>
                </select>
                <div class="leverage_item" style={{
                  filter: (circle_benefit_mode !== 'signal') ? 'blur(5px)' : 'none',
                }}>순환매 익절 기준</div>
                <select className="select_samaria"
                  value={circle_close_mode}
                  disabled={circle_benefit_mode !== 'signal'}
                  style={{
                    filter: (circle_benefit_mode !== 'signal') ? 'blur(5px)' : 'none',
                  }}
                  onChange={handleCircleCloseMode}>
                  <option value="ut">시그널</option>
                  <option value="kinpoint">킨포인트</option>
                </select>
              <div class="leverage_item" style={{
                  filter: (circle_benefit_mode !== 'signal') ? 'blur(5px)' : 'none',
                }}>순환매 익절기준봉</div>
                  <select className="select_samaria" value={circle_candle}
                  disabled={circle_benefit_mode !== 'signal'}
                  style={{
                    filter: (circle_benefit_mode !== 'signal') ? 'blur(5px)' : 'none',
                  }}
                  onChange={handleCircleCandle}>
                  <option value="5m">5 분봉</option>
                  <option value="15m">15 분봉</option>
                  <option value="30m">30 분봉</option>
                  <option value="1h">1 시간봉</option>
                  <option value="4h">4 시간봉</option>
                  <option value="6h">6 시간봉</option>
                  <option value="12h">12 시간봉</option>
                  <option value="1d">1 일봉</option>
                  <option value="3d">3 일봉</option>
                  <option value="1w">1 주봉</option>
                  </select>
                </div>
              <div class="leverage_container">
                <div class="leverage_item"style={{
                  filter: (circle_close_mode !== 'ut' || circle_benefit_mode !== 'signal') ? 'blur(5px)' : 'none',
                }}>LONG Sensitivity</div>
                <input
                type="number"
                step="1"
                class="leverage_input_basic"
                value={sensitivity_circle}
                disabled={circle_close_mode !== 'ut' || circle_benefit_mode !== 'signal'}
                style={{
                  filter: (circle_close_mode !== 'ut' || circle_benefit_mode !== 'signal') ? 'blur(5px)' : 'none',
                }}
                onChange={handleSensitivityCircle} />
                <div class="leverage_item"style={{
                  filter: (circle_close_mode !== 'ut' || circle_benefit_mode !== 'signal') ? 'blur(5px)' : 'none',
                }}>LONG ATR Period</div>
                <input
                type="number"
                step="1"
                class="leverage_input_basic"
                value={atr_period_circle}
                disabled={circle_close_mode !== 'ut' || circle_benefit_mode !== 'signal'}
                style={{
                  filter: (circle_close_mode !== 'ut' || circle_benefit_mode !== 'signal') ? 'blur(5px)' : 'none',
                }}
                onChange={handleAtrperiodCircle} />
              </div>
              <div class="leverage_container">
                <div class="leverage_item"style={{
                  filter: (circle_close_mode !== 'ut' || circle_benefit_mode !== 'signal') ? 'blur(5px)' : 'none',
                }}>SHORT Sensitivity</div>
                <input
                type="number"
                step="1"
                class="leverage_input_basic"
                value={sensitivity_circle_short}
                disabled={circle_close_mode !== 'ut' || circle_benefit_mode !== 'signal'}
                style={{
                  filter: (circle_close_mode !== 'ut' || circle_benefit_mode !== 'signal') ? 'blur(5px)' : 'none',
                }}
                onChange={handleSensitivityCircleShort} />
                <div class="leverage_item"style={{
                  filter: (circle_close_mode !== 'ut' || circle_benefit_mode !== 'signal') ? 'blur(5px)' : 'none',
                }}>SHORT ATR Period</div>
                <input
                type="number"
                step="1"
                class="leverage_input_basic"
                value={atr_period_circle_short}
                disabled={circle_close_mode !== 'ut' || circle_benefit_mode !== 'signal'}
                style={{
                  filter: (circle_close_mode !== 'ut' || circle_benefit_mode !== 'signal') ? 'blur(5px)' : 'none',
                }}
                onChange={handleAtrperiodCircleShort} />
              </div>
              <div class="leverage_container">
                <div class="leverage_item"style={{
                  filter: (circle_close_mode !== 'ut' || circle_benefit_mode !== 'signal') ? 'blur(5px)' : 'none',
                }}>순환매 익절 기준 캔들</div>
                  <select className="select_samaria"
                    value={candle_type_circle}
                    disabled={circle_close_mode !== 'ut' || circle_benefit_mode !== 'signal'}
                    style={{
                      filter: (circle_close_mode !== 'ut' || circle_benefit_mode !== 'signal') ? 'blur(5px)' : 'none',
                    }}
                    onChange={handleCandleTypeCircle}>
                    <option value="haikin">하이킨아시</option>
                    <option value="normal">일반봉</option>
                  </select>
                <div class="leverage_item" style={{
                  filter: (circle_benefit_mode !== 'signal') ? 'blur(5px)' : 'none',
                }}>익절 기준 신호</div>
                  <select className="select_samaria"
                    value={circle_checking}
                    disabled={circle_benefit_mode !== 'signal'}
                    style={{
                      filter: (circle_benefit_mode !== 'signal') ? 'blur(5px)' : 'none',
                    }}
                    onChange={handleCircleChecking}>
                    <option value="-1">발생</option>
                    <option value="-2">확정</option>
                  </select>
              </div>

              <div className="line"></div>

              <div className="center_text_lever">Close benefit setting</div>

              <div class="leverage_container">
              <div class="leverage_item">단일 익절 기준</div>
                <select className="select_samaria"
                  value={close_mode}
                  onChange={handleCloseMode}>
                  <option value="ut">시그널</option>
                  <option value="kinpoint">킨포인트</option>
                </select>
                <div class="leverage_item">익절 기준봉</div>
                  <select className="select_samaria" disabled={close_mode === 'max_benefit'} value={close_candle}
                  style={{
                    filter: (close_mode === 'max_benefit') ? 'blur(5px)' : 'none',
                  }}
                  onChange={handleCloseCandle}>
                  <option value="5m">5 분봉</option>
                  <option value="15m">15 분봉</option>
                  <option value="30m">30 분봉</option>
                  <option value="1h">1 시간봉</option>
                  <option value="4h">4 시간봉</option>
                  <option value="6h">6 시간봉</option>
                  <option value="12h">12 시간봉</option>
                  <option value="1d">1 일봉</option>
                  <option value="3d">3 일봉</option>
                  <option value="1w">1 주봉</option>
                  </select>
                </div>
                <div class="leverage_container">
                <div class="leverage_item"style={{
                  filter: (close_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}>LONG Sensitivity</div>
                <input
                type="number"
                step="1"
                class="leverage_input_basic"
                value={sensitivity_close}
                disabled={close_mode !== 'ut'}
                style={{
                  filter: (close_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}
                onChange={handleSensitivityClose} />
                <div class="leverage_item"style={{
                  filter: (close_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}>LONG ATR Period</div>
                <input
                type="number"
                step="1"
                class="leverage_input_basic"
                value={atr_period_close}
                disabled={close_mode !== 'ut'}
                style={{
                  filter: (close_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}
                onChange={handleAtrperiodClose} />
              </div>
              <div class="leverage_container">
                <div class="leverage_item"style={{
                  filter: (close_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}>SHORT Sensitivity</div>
                <input
                type="number"
                step="1"
                class="leverage_input_basic"
                value={sensitivity_close_short}
                disabled={close_mode !== 'ut'}
                style={{
                  filter: (close_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}
                onChange={handleSensitivityCloseShort} />
                <div class="leverage_item"style={{
                  filter: (close_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}>SHORT ATR Period</div>
                <input
                type="number"
                step="1"
                class="leverage_input_basic"
                value={atr_period_close_short}
                disabled={close_mode !== 'ut'}
                style={{
                  filter: (close_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}
                onChange={handleAtrperiodCloseShort} />
              </div>
              <div class="leverage_container">
                <div class="leverage_item"style={{
                  filter: (close_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}>익절 기준 캔들</div>
                  <select className="select_samaria"
                    value={candle_type_close}
                    disabled={close_mode !== 'ut'}
                    style={{
                      filter: (close_mode !== 'ut') ? 'blur(5px)' : 'none',
                    }}
                    onChange={handleCandleTypeClose}>
                    <option value="haikin">하이킨아시</option>
                    <option value="normal">일반봉</option>
                  </select>
                <div class="leverage_item" style={{
                  filter: (close_mode === 'max_benefit') ? 'blur(5px)' : 'none',
                }}>익절 기준 신호</div>
                  <select className="select_samaria"
                    value={close_checking}
                    disabled={close_mode === 'max_benefit'}
                    style={{
                      filter: (close_mode === 'max_benefit') ? 'blur(5px)' : 'none',
                    }}
                    onChange={handleCloseChecking}>
                    <option value="-1">발생</option>
                    <option value="-2">확정</option>
                  </select>
                  <div class="leverage_item">익절 후 연속 진입</div>
                  <select className="select_samaria" value={single_tp_continue_mode} onChange={handleSingleTpContinueMode}>
                    <option value="disable">미적용</option>
                    <option value="able">적용</option>
                  </select>
              </div>

              <div className="line"></div>

              <div className="leverage_container">
                <div class="leverage_item">Set Leverage</div>
                  <input
                  type="number"
                  class="leverage_input_basic"
                  step="1"
                  value={leverage_info}
                  onChange={handleLeverage} />
                <div class="leverage_item">최소 익절률(%)</div>
                  <input
                  type="number"
                  class="leverage_input_basic"
                  value={benefit_ratio}
                  onChange={handleBenefitratio} />
                <div class="leverage_item" disabled={both_mode === 'double'}
                    style={{
                      filter: (both_mode === 'double') ? 'blur(5px)' : 'none',
                    }}>진입 방향 선택</div>
                  <select className="select_samaria" value={direction} onChange={handleDirection}
                  disabled={both_mode === 'double'}
                    style={{
                      filter: (both_mode === 'double') ? 'blur(5px)' : 'none',
                    }}>
                    <option value='long'>롱</option>
                    <option value='short'>숏</option>
                    <option value='auto'disabled={entry_standard !== 'able'}
                    className={entry_standard !== 'able' ? "blurred-option" : ""}>자동전환</option>
                  </select>
              </div>

            <div class="leverage_container">
              <div class="leverage_item" disabled={both_mode !== 'double'}
                  style={{
                    filter: (both_mode !== 'double') ? 'blur(5px)' : 'none',
                  }}>복리 모드 선택</div>
                <select className="select_samaria" value={welfare_mode} onChange={handleWelfareMode}
                    disabled={both_mode !== 'double'}
                    style={{
                      filter: (both_mode !== 'double') ? 'blur(5px)' : 'none',
                    }}>
                    <option value='disable'>미적용</option>
                    <option value='able'>적용</option>
                  </select>
                <div class="leverage_item" disabled={both_mode !== 'double' || welfare_mode === 'disable'}
                    style={{
                      filter: (both_mode !== 'double' || welfare_mode === 'disable') ? 'blur(5px)' : 'none',
                    }}>진입 비율(%)</div>
                  <input
                  type="number"
                  class="leverage_input_basic"
                  disabled={both_mode !== 'double' || welfare_mode === 'disable'}
                  style={{
                    filter: (both_mode !== 'double' || welfare_mode === 'disable') ? 'blur(5px)' : 'none',
                  }}
                  value={welfare_ratio}
                  onChange={handleWelfareRatio} />
              <div className="leverage_item">진입 USDT</div>
                <input
                type="number"
                step="1"
                class="leverage_input_basic"
                disabled={welfare_mode === 'able' && both_mode === 'double'}
                value={entry_usdt_1}
                onChange={handleEntryUsdt_1} />
              </div>
              
              <div class="leverage_container">
                <div class="leverage_item">RealizedPnL</div>
                    <input
                    type="number"
                    class="leverage_input_basic"
                    disabled={true}
                    value={totalpnl} />
                <div class="leverage_item">헷지 연결 포지션</div>
                  <select className="select_samaria" value={hedge_continue} onChange={handleHedgeContinue}
                      disabled={mode === 'auto'}
                      style={{
                        filter: (mode === 'auto') ? 'blur(5px)' : 'none',
                        }}>
                    <option value='no'>미적용</option>
                    <option value='ok'>적용</option>
                  </select>
              </div>

              <div className="line"></div>
            <div className="center_text_lever" style={{
              filter: (both_mode !== 'double') ? 'blur(5px)' : 'none',
            }}>심볼별 더블익절 셋팅</div>
            <div className="leverage_container">
                <div class="leverage_item" disabled={both_mode !== 'double'}
                    style={{
                      filter: (both_mode !== 'double') ? 'blur(5px)' : 'none',
                    }}>더블익절설정(%)</div>
                  <input
                  type="number"
                  class="leverage_input_basic"
                  disabled={both_mode !== 'double'}
                  style={{
                    filter: (both_mode !== 'double') ? 'blur(5px)' : 'none',
                  }}
                  value={double_benefit}
                  onChange={handleDoubleBenefit} />
                <div class="leverage_item" disabled={both_mode !== 'double'}
                    style={{
                      filter: (both_mode !== 'double') ? 'blur(5px)' : 'none',
                    }}>현재수익(USDT)</div>
                  <input
                  type="number"
                  class="leverage_input_basic"
                  style={{
                    filter: (both_mode !== 'double') ? 'blur(5px)' : 'none',
                  }}
                  value={double_pnl}
                  disabled = {true} />
                <div class="leverage_item" disabled={both_mode !== 'double'}
                    style={{
                      filter: (both_mode !== 'double') ? 'blur(5px)' : 'none',
                    }}>더블연속모드</div>
                  <select className="select_samaria" value={double_continue} onChange={handleDoubleContinue}
                    disabled={both_mode !== 'double'}
                    style={{
                      filter: (both_mode !== 'double') ? 'blur(5px)' : 'none',
                    }}>
                    <option value="disable">미적용</option>
                    <option value="able">적용</option>
                  </select>
              </div>

            <div className="line"></div>
            <div className="center_text_lever">잔고 조회 및 종합익절 셋팅</div>
              <div class="leverage_container">
                <div class="leverage_item">메인 잔고 조회(USDT)</div>
                <input
                type="number"
                class="leverage_input_basic"
                value={main_balance}
                disabled = {true} />
                <div class="leverage_item"  disabled={both_mode !== 'double'}
                    style={{
                      filter: (both_mode !== 'double') ? 'blur(5px)' : 'none',
                    }}>2nd 잔고 조회(USDT)</div>
                <input
                type="number"
                class="leverage_input_basic"
                style={{
                      filter: (both_mode !== 'double') ? 'blur(5px)' : 'none',
                    }}
                value={second_balance}
                disabled = {true} />
                <div class="leverage_item">총합 잔고(USDT)</div>
                <input
                type="number"
                class="leverage_input_basic"
                value={Number(main_balance + second_balance).toFixed(3)}
                disabled = {true} />
              </div>
               <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                <button onClick={() => onClickMainBalance(symbol)} className="button_benefit_c_lever">
                메인 잔고 조회
                </button>
                <button onClick={() => onClickSecondBalance(symbol)} className="button_benefit_c_lever"
                  disabled={both_mode !== 'double'}
                    style={{
                      filter: (both_mode !== 'double') ? 'blur(5px)' : 'none',
                    }}>
                2nd 잔고 조회
                </button>
                <button onClick={() => onClickDoublePnl(symbol)} className="button_benefit_c_lever"
                  disabled={both_mode !== 'double'}
                    style={{
                      filter: (both_mode !== 'double') ? 'blur(5px)' : 'none',
                    }}>
                더블수익 조회
                </button>
              </div>
              <div class="leverage_container">
                <div class="leverage_item">종합 익절 셋팅(%)</div>
                <input
                type="number"
                class="leverage_input_basic"
                value={total_benefit_usdt}
                onChange={handleTotalBenefitusdt} />
                <div class="leverage_item">종합 익절 목표값(USDT)</div>
                <input
                type="number"
                class="leverage_input_basic"
                value={Number(total_benefit_usdt_obj).toFixed(3)}
                disabled = {true} />
              </div>
              <div class="leverage_container">
                <div class="leverage_item">종합 익절 목표 횟수</div>
                <input
                type="number"
                class="leverage_input_basic"
                value={total_benefit_count}
                onChange={handleTotalBenefitCount} />
                <div class="leverage_item">종합 익절 현재 횟수</div>
                <input
                type="number"
                class="leverage_input_basic"
                value={total_current_count}
                disabled = {true} />
              </div>
              <div class="leverage_container">
                <div class="leverage_item">종합 익절 연속 모드</div>
                <select className="select_samaria" value={total_continue_mode} onChange={handleTotalContinueMode}>
                  <option value="disable">미적용</option>
                  <option value="able">적용</option>
                </select>
                <div class="leverage_item">연속 모드 시 진행 방향</div>
                <select className="select_samaria" value={total_continue_direction} onChange={handleTotalContinueDirection}>
                  <option value="both">양방향</option>
                  <option value="long">롱</option>
                  <option value="short">숏</option>
                </select>
              </div>
               <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                <button onClick={() => onClickTotal_search(symbol)} className="button_benefit_c_lever">
                종합 익절 목표값 확인
                </button>
                <button onClick={() => onClickTotal_start(symbol)} className="button_benefit_c_lever">
                종합 익절 시작
                </button>
                <button onClick={() => onClickTotal_cancel(symbol)} className="button_benefit_c_lever">
                종합 익절 취소
                </button>
              </div>
              <div className="leverage_container">
                <div
                  className={`leverage_item total-benefit-status ${isTotalBenefitRunning ? 'is-running' : 'is-stopped'}`}
                >
                  {isTotalBenefitRunning ? '종합익절 구동중' : '종합익절 정지중'}
                </div>
              </div>
                </div>{/* contentWrap_sp 닫힘 */}
              <div className="line"></div>
              <OpenPositionsSnapshotSection
                endpoint="/hold_open_positions_snapshot"
                enabled={Boolean(uid)}
                reloadKey={`${uid}-${targetUid}-${isAdminAccess ? "1" : "0"}-${snapshotReloadToken}`}
                fetchImpl={fetchHold}
                title="메인/보조 오픈 포지션"
                updatedAtPrefix="마지막 갱신:"
                loadingRefreshLabel="조회중..."
                onSelectSymbol={onClickCoin}
                selectedSymbol={symbol}
                showAdminManualTrade={showAdminManualTrade}
                onAdminNewEntry={
                  showAdminManualTrade
                    ? (e) => setAdminManualModal({
                        mode: "new",
                        anchorRect: snapshotAnchorRect(e.currentTarget),
                      })
                    : undefined
                }
                onAdminAdjustLeg={
                  showAdminManualTrade
                    ? (payload) => setAdminManualModal({
                        mode: "leg",
                        account: payload.account,
                        item: payload.item,
                        anchorRect: payload.anchorRect ?? null,
                      })
                    : undefined
                }
                onHasSecondAccountChange={setSnapshotHasSecondAccount}
              />
              {showAdminManualTrade && adminManualModal ? (
                <HoldAdminManualModal
                  open
                  payload={adminManualModal}
                  onClose={() => setAdminManualModal(null)}
                  onOrderSuccess={() => setSnapshotReloadToken((t) => t + 1)}
                  fetchHold={fetchHold}
                  hasSecondAccount={both_mode === "double" || snapshotHasSecondAccount}
                  pageSymbol={symbol}
                  selectableSymbols={selectableSymbols}
                  holdTargetUid={targetUid}
                  isAdminAccess={isAdminAccess}
                  pageLeverage={leverage_info}
                />
              ) : null}
              <div className="line"></div>
              
            {/* <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                <button onClick={() => onClickAutoStart()} disabled={mode === 'normal'}
                style={{
                    filter: (mode === 'normal') ? 'blur(5px)' : 'none',
                  }} className="button_benefit_c_lever">
                자동 매매 시작
                </button>
                <button onClick={() => onClickStop()} className="button_benefit_c_lever">
                자동 매매 종료
                </button>
            </div>   */}
                {/* ── 진입 제한 설정 버튼 (관리자 + 심볼 선택 시) ── */}
            {isAdminAccess && symbol && symbol !== 'default' && (
              <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                <button
                  onClick={openHoldRestrictModal}
                  className="bottomButton_sp_lever"
                  style={{ background: 'rgba(231,76,60,0.15)', border: '1px solid rgba(231,76,60,0.5)', color: '#e74c3c', transition: 'background 0.18s, border-color 0.18s, box-shadow 0.18s' }}
                  onMouseEnter={e => { e.currentTarget.style.background='rgba(231,76,60,0.35)'; e.currentTarget.style.borderColor='rgba(231,76,60,0.9)'; e.currentTarget.style.boxShadow='0 0 8px rgba(231,76,60,0.4)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background='rgba(231,76,60,0.15)'; e.currentTarget.style.borderColor='rgba(231,76,60,0.5)'; e.currentTarget.style.boxShadow='none'; }}
                >
                  진입 제한 시간 설정
                </button>
              </div>
            )}

            <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                <button onClick={() => onClickStart()} disabled={mode === 'auto'}
                style={{
                    filter: (mode === 'auto') ? 'blur(5px)' : 'none',
                  }} className="bottomButton_sp_lever">
                일반 매매 시작
                </button>
            </div>

            <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                <button onClick={() => onClickBenefit_stop(symbol)} disabled={benefit_stop === 1 || trade_start === 0 || benefit_stop === '1' || trade_start === '0'} className="button_benefit_c_lever">
                익절 후 정리
                </button>
                <button onClick={() => onClickBenefit_cancel(symbol)} disabled={benefit_stop === '0' || benefit_stop === 0} className="button_benefit_c_lever">
                취 소
                </button>
            </div>

            <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                <button onClick={() => onClickCurrentCoin_stop(symbol)} disabled={(symbol === 'default' && trade_start === 0 &&  benefit_stop === 0) || (symbol === 'default' && trade_start === '0' &&  benefit_stop === '0') || setlevel <= 7} className="bottomButton_sp_lever">
                현재 코인 정리
                </button>
            </div>

            <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                <button onClick={() => onClickEnd()} disabled={notAllow} className="bottomButton_sp_lever">
                로그아웃
                </button>
             </div>
              </article>
            </div>

            <aside className="kp-console__side">
              <article className="kp-card kp-card--list">
                <div className="list_box_title_lever">진입중인 코인 리스트</div>
                <ul className="list_box_lever">
                  {e_items.map((item, index) => (
                    <li key={index} className="list_item" onClick={() => onClickCoin(item)}>
                      {item}
                    </li>
                  ))}
                </ul>
              </article>

              <article className="kp-card kp-card--list">
                <div className="list_box_title_lever">익절 후 정리 리스트</div>
                <ul className="list_box_lever">
                  {w_items.map((item, index) => (
                    <li key={index} className="list_item" onClick={() => onClickCoin(item)}>
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
            </aside>
          </div>
        </section>

      {holdRestrictModalOpen && holdRestrictDraft && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={closeHoldRestrictModal}
          role="presentation"
        >
          <div
            style={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: '12px', padding: '28px', width: '360px', maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: '20px' }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <p style={{ margin: 0, fontSize: '0.75rem', color: '#aaa', letterSpacing: '0.05em' }}>Hold / {symbol}</p>
                <h3 style={{ margin: '4px 0 0', fontSize: '1rem', color: '#fff' }}>진입 제한 시간대 설정</h3>
              </div>
              <button type="button" onClick={closeHoldRestrictModal} style={{ background: 'none', border: 'none', color: '#aaa', fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1, transition: 'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color='#fff'}
                  onMouseLeave={e => e.currentTarget.style.color='#aaa'}>×</button>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={holdRestrictDraft.restrict_enabled}
                onChange={handleHoldRestrictDraftChange('restrict_enabled')}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 600, color: '#fff' }}>제한 시간대 활성화</span>
              {holdRestrictDraft.restrict_enabled
                ? <span style={{ color: '#e74c3c', fontWeight: 700, fontSize: '0.85rem' }}>ON</span>
                : <span style={{ color: '#888', fontSize: '0.85rem' }}>OFF</span>}
            </label>

            {holdRestrictDraft.restrict_enabled && (
              <>
                {/* ── 프리셋 불러오기 ── */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span style={{ fontSize: '0.82rem', color: '#aaa' }}>프리셋</span>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <select
                      value={holdRestrictSelectedPresetId}
                      onChange={(e) => setHoldRestrictSelectedPresetId(e.target.value)}
                      style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid #444', background: '#111', color: holdRestrictSelectedPresetId ? '#fff' : '#777' }}
                    >
                      <option value="">-- 프리셋 선택 --</option>
                      {holdRestrictPresets.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => {
                        if (!holdRestrictSelectedPresetId) return;
                        const p = holdRestrictPresets.find((x) => String(x.id) === String(holdRestrictSelectedPresetId));
                        if (!p) return;
                        setHoldRestrictDraft((prev) => ({
                          ...prev,
                          restrict_start_day:  p.start_day,
                          restrict_start_time: p.start_time,
                          restrict_end_day:    p.end_day,
                          restrict_end_time:   p.end_time,
                        }));
                        setHoldRestrictPresetMsg('');
                      }}
                      style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #555', background: 'rgba(255,255,255,0.08)', color: '#ccc', cursor: holdRestrictSelectedPresetId ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap', transition: 'background 0.15s, border-color 0.15s', opacity: holdRestrictSelectedPresetId ? 1 : 0.4 }}
                      onMouseEnter={e => { if (!holdRestrictSelectedPresetId) return; e.currentTarget.style.background='rgba(255,255,255,0.18)'; e.currentTarget.style.borderColor='#888'; }}
                      onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor='#555'; }}
                    >
                      불러오기
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!holdRestrictSelectedPresetId) return;
                        if (!window.confirm('선택한 프리셋을 삭제할까요?')) return;
                        const res = await fetchHold(`/admin/hold_restrict_presets/${holdRestrictSelectedPresetId}`, { method: 'DELETE' });
                        if (res.ok) {
                          setHoldRestrictPresets((prev) => prev.filter((x) => String(x.id) !== String(holdRestrictSelectedPresetId)));
                          setHoldRestrictSelectedPresetId('');
                          setHoldRestrictPresetMsg('🗑 삭제됐습니다');
                        }
                      }}
                      style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid rgba(231,76,60,0.4)', background: 'rgba(231,76,60,0.08)', color: '#e74c3c', cursor: holdRestrictSelectedPresetId ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap', transition: 'background 0.15s, border-color 0.15s', opacity: holdRestrictSelectedPresetId ? 1 : 0.4 }}
                      onMouseEnter={e => { if (!holdRestrictSelectedPresetId) return; e.currentTarget.style.background='rgba(231,76,60,0.22)'; e.currentTarget.style.borderColor='rgba(231,76,60,0.8)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background='rgba(231,76,60,0.08)'; e.currentTarget.style.borderColor='rgba(231,76,60,0.4)'; }}
                    >
                      삭제
                    </button>
                  </div>
                  {/* ── 현재 설정 저장 ── */}
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input
                      type="text"
                      placeholder="프리셋 이름 입력"
                      value={holdRestrictPresetName}
                      onChange={(e) => setHoldRestrictPresetName(e.target.value)}
                      style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid #444', background: '#111', color: '#fff' }}
                    />
                    <button
                      type="button"
                      onClick={async () => {
                        if (!holdRestrictPresetName.trim()) return;
                        const res = await fetchHold('/admin/hold_restrict_presets', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            name:       holdRestrictPresetName.trim(),
                            start_day:  holdRestrictDraft.restrict_start_day,
                            start_time: holdRestrictDraft.restrict_start_time,
                            end_day:    holdRestrictDraft.restrict_end_day,
                            end_time:   holdRestrictDraft.restrict_end_time,
                          }),
                        });
                        const data = await res.json();
                        if (res.ok && data.ok) {
                          setHoldRestrictPresets((prev) => [...prev, {
                            id: data.id, name: holdRestrictPresetName.trim(),
                            start_day:  holdRestrictDraft.restrict_start_day,
                            start_time: holdRestrictDraft.restrict_start_time,
                            end_day:    holdRestrictDraft.restrict_end_day,
                            end_time:   holdRestrictDraft.restrict_end_time,
                          }]);
                          setHoldRestrictPresetName('');
                          setHoldRestrictPresetMsg('✅ 저장됐습니다');
                        } else {
                          setHoldRestrictPresetMsg(`❌ ${data.error || '저장 실패'}`);
                        }
                      }}
                      style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #555', background: 'rgba(255,255,255,0.08)', color: '#ccc', cursor: holdRestrictPresetName.trim() ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap', transition: 'background 0.15s, border-color 0.15s', opacity: holdRestrictPresetName.trim() ? 1 : 0.4 }}
                      onMouseEnter={e => { if (!holdRestrictPresetName.trim()) return; e.currentTarget.style.background='rgba(255,255,255,0.18)'; e.currentTarget.style.borderColor='#888'; }}
                      onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor='#555'; }}
                    >
                      저장
                    </button>
                  </div>
                  {holdRestrictPresetMsg && (
                    <p style={{ margin: 0, fontSize: '0.8rem', color: holdRestrictPresetMsg.startsWith('✅') ? '#2ecc71' : holdRestrictPresetMsg.startsWith('🗑') ? '#aaa' : '#e74c3c' }}>
                      {holdRestrictPresetMsg}
                    </p>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span style={{ fontSize: '0.82rem', color: '#aaa' }}>시작</span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <select value={holdRestrictDraft.restrict_start_day} onChange={handleHoldRestrictDraftChange('restrict_start_day')}
                      style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid #444', background: '#111', color: '#fff' }}>
                      {HOLD_DAY_LABELS.map((label, idx) => <option key={idx} value={idx}>{label}요일</option>)}
                    </select>
                    <input type="time" value={holdRestrictDraft.restrict_start_time} onChange={handleHoldRestrictDraftChange('restrict_start_time')}
                      style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid #444', background: '#111', color: '#fff' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span style={{ fontSize: '0.82rem', color: '#aaa' }}>종료</span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <select value={holdRestrictDraft.restrict_end_day} onChange={handleHoldRestrictDraftChange('restrict_end_day')}
                      style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid #444', background: '#111', color: '#fff' }}>
                      {HOLD_DAY_LABELS.map((label, idx) => <option key={idx} value={idx}>{label}요일</option>)}
                    </select>
                    <input type="time" value={holdRestrictDraft.restrict_end_time} onChange={handleHoldRestrictDraftChange('restrict_end_time')}
                      style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid #444', background: '#111', color: '#fff' }} />
                  </div>
                </div>
                <p style={{ fontSize: '0.82rem', color: '#aaa', margin: 0, lineHeight: 1.6 }}>
                  📅 <strong style={{ color: '#fff' }}>
                    {HOLD_DAY_LABELS[holdRestrictDraft.restrict_start_day]}요일 {holdRestrictDraft.restrict_start_time}
                    &nbsp;~&nbsp;
                    {HOLD_DAY_LABELS[holdRestrictDraft.restrict_end_day]}요일 {holdRestrictDraft.restrict_end_time}
                  </strong>
                  <br />이 시간대에 신규 진입 차단 + 반대방향 포지션 정리
                </p>
              </>
            )}

            {holdRestrictSaveMsg && (
              <p style={{ margin: 0, fontSize: '0.9rem', color: holdRestrictSaveMsg.startsWith('✅') ? '#2ecc71' : '#e74c3c' }}>{holdRestrictSaveMsg}</p>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={closeHoldRestrictModal} disabled={holdRestrictSaveLoading}
                style={{ padding: '8px 18px', borderRadius: '8px', border: '1px solid #555', background: 'transparent', color: '#ccc', cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor='#888'; }}
                onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.borderColor='#555'; }}>
                취소
              </button>
              <button type="button" onClick={handleHoldRestrictSave} disabled={holdRestrictSaveLoading}
                style={{ padding: '8px 18px', borderRadius: '8px', border: 'none', background: '#e74c3c', color: '#fff', fontWeight: 700, cursor: 'pointer', transition: 'background 0.15s, box-shadow 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background='#c0392b'; e.currentTarget.style.boxShadow='0 0 8px rgba(231,76,60,0.5)'; }}
                onMouseLeave={e => { e.currentTarget.style.background='#e74c3c'; e.currentTarget.style.boxShadow='none'; }}>
                {holdRestrictSaveLoading ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
      </main>
    );
}
 